import React, { useState, useEffect, useCallback } from 'react';
import {
    RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Eye, Edit, Save, Plus,
    Search, Building, CreditCard, Banknote, Wallet, Brain, Send, X, Check,
    DollarSign, Calendar, Receipt, User, Package, MapPin, Phone, Mail,
    ChevronDown, ChevronUp, Filter, Download, Upload, Settings, FileText,
    Users, Globe, Hash, Briefcase, CreditCard as Card, Shield, AlertCircle,
    Copy, ExternalLink, File, Percent, Calculator, Clock4
} from 'lucide-react';
import VendorManagement from './CreateVendorModal';

const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }

    try {
        // Handle date objects or other date strings
        const date = new Date(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            console.warn('Invalid date provided to formatDateForInput:', dateString);
            return '';
        }

        // Use local date parts to avoid timezone issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    } catch (error) {
        console.warn('Error formatting date:', error);
        return '';
    }
};

// Enhanced GST Tax Validation Functions
const validateGSTTax = (purchase, vendor) => {
    const validation = {
        isValid: true,
        errors: [],
        warnings: [],
        taxDetails: null
    };

    const sgstRate = parseFloat(purchase.sgst || 0);
    const cgstRate = parseFloat(purchase.cgst || 0);
    const igstRate = parseFloat(purchase.igst || 0);
    const amount = parseFloat(purchase.amount || 0);
    const total = parseFloat(purchase.total || 0);

    // Calculate expected tax amounts
    const expectedSgstAmount = (amount * sgstRate) / 100;
    const expectedCgstAmount = (amount * cgstRate) / 100;
    const expectedIgstAmount = (amount * igstRate) / 100;
    const totalTaxAmount = expectedSgstAmount + expectedCgstAmount + expectedIgstAmount;
    const expectedTotal = amount + totalTaxAmount;

    // Rule 1: Tax Structure Validation
    if (igstRate > 0 && (sgstRate > 0 || cgstRate > 0)) {
        validation.isValid = false;
        validation.errors.push('Cannot have both IGST and SGST/CGST. Use either IGST or SGST+CGST combination.');
    }

    // Rule 2: SGST + CGST should be equal
    if (sgstRate > 0 && cgstRate > 0 && sgstRate !== cgstRate) {
        validation.isValid = false;
        validation.errors.push(`SGST rate (${sgstRate}%) and CGST rate (${cgstRate}%) should be equal.`);
    }

    // Rule 3: Standard GST Rates Validation
    const validGstRates = [0, 0.25, 3, 5, 12, 18, 28];
    const totalRate = igstRate || (sgstRate + cgstRate);

    if (totalRate > 0 && !validGstRates.includes(totalRate)) {
        validation.warnings.push(`GST rate ${totalRate}% is not a standard rate.`);
    }

    // Vendor State vs Tax Type Validation
    if (vendor && vendor.place_of_supply) {
        const vendorState = vendor.place_of_supply;
        const companyState = 'Tamil Nadu';

        if (vendorState === companyState) {
            if (igstRate > 0) {
                validation.warnings.push(`Intra-state transaction should use SGST+CGST instead of IGST.`);
            }
        } else {
            if (sgstRate > 0 || cgstRate > 0) {
                validation.warnings.push(`Inter-state transaction should use IGST instead of SGST+CGST.`);
            }
        }
    }

    // Set tax details
    if (igstRate > 0) {
        validation.taxDetails = {
            type: 'IGST',
            rate: igstRate,
            amount: expectedIgstAmount
        };
    } else if (sgstRate > 0 && cgstRate > 0) {
        validation.taxDetails = {
            type: 'SGST+CGST',
            sgstRate: sgstRate,
            cgstRate: cgstRate,
            totalRate: sgstRate + cgstRate,
            sgstAmount: expectedSgstAmount,
            cgstAmount: expectedCgstAmount,
            totalTaxAmount: expectedSgstAmount + expectedCgstAmount
        };
    }

    return validation;
};

// Check for duplicate invoices
const checkForDuplicateInvoice = (purchase, allPurchases) => {
    if (!purchase.invoice_no || !purchase.vendor_name) return false;

    return allPurchases.some(p =>
        p.id !== purchase.id &&
        p.invoice_no === purchase.invoice_no &&
        p.vendor_name === purchase.vendor_name &&
        (p.zoho_bill_id || p.zoho_sync_status === 'synced')
    );
};

// Check if bill date is in future
const isFutureBillDate = (billDate) => {
    if (!billDate) return false;
    const today = new Date();
    const bill = new Date(billDate);
    return bill > today;
};

// Main Component
const PurchaseBillsIntegrationManager = () => {
    // Main component state
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    const [tdsRates, setTdsRates] = useState([]); // FIXED: Load from local taxes table
    const [filter, setFilter] = useState('all');
    const [selectedPurchases, setSelectedPurchases] = useState([]);
    const [syncInProgress, setSyncInProgress] = useState(false);
    const [editingPurchase, setEditingPurchase] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [newVendorCreated, setNewVendorCreated] = useState(null);

    // Enhanced vendor search with auto-creation option
    const [vendorSearchResults, setVendorSearchResults] = useState([]);
    const [vendorSearchLoading, setVendorSearchLoading] = useState(false);
    const [showAutoCreateOption, setShowAutoCreateOption] = useState(false);
    const [autoCreatingVendor, setAutoCreatingVendor] = useState(false);

    // Add this new state for auto-processing
    const [autoProcessing, setAutoProcessing] = useState(false);
    const [autoProcessResults, setAutoProcessResults] = useState(null);

    // Organization state
    const [currentOrganization, setCurrentOrganization] = useState({
        organization_id: '',
        name: ''
    });

    // Add the auto-processing function
    const autoProcessPurchases = async () => {
        if (!currentOrganization?.organization_id) {
            alert('No organization available. Please refresh the page.');
            return;
        }

        setAutoProcessing(true);

        try {
            console.log('🤖 Starting auto-processing for company:', currentOrganization.organization_id);

            const response = await fetch(`${API_BASE_URL}/purchases/auto-process?company_id=${currentOrganization.organization_id}`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setAutoProcessResults(data);

                // Refresh purchases to show updated data
                await fetchPurchases();

                // Show success message
                const { successful, failed, total } = data.summary;
                alert(`Auto-processing completed!\n✅ Successful: ${successful}\n❌ Failed: ${failed}\n📊 Total: ${total}`);

                console.log('🎉 Auto-processing results:', data.results);
            } else {
                throw new Error(data.message || 'Auto-processing failed');
            }
        } catch (error) {
            console.error('❌ Auto-processing error:', error);
            alert('Auto-processing failed: ' + error.message);
        } finally {
            setAutoProcessing(false);
        }
    };

    // Enhanced validation function that matches backend logic
    const calculateValidationStatus = (purchase, allPurchases) => {
        const missingFields = [];

        // Check for duplicates
        const isDuplicate = checkForDuplicateInvoice(purchase, allPurchases);
        if (isDuplicate) {
            missingFields.push({ field: 'duplicate_invoice', label: 'Duplicate Invoice - Already synced' });
        }

        // Basic field validation
        if (!purchase.vendor_name) missingFields.push({ field: 'vendor_name', label: 'Vendor Name' });
        if (!purchase.invoice_no) missingFields.push({ field: 'invoice_no', label: 'Invoice Number' });
        if (!purchase.date) missingFields.push({ field: 'date', label: 'Invoice Date' });
        if (!purchase.item) missingFields.push({ field: 'item', label: 'Item Description' });
        if (!purchase.total || purchase.total <= 0) missingFields.push({ field: 'total', label: 'Total Amount' });
        if (!purchase.zoho_vendor_id) missingFields.push({ field: 'zoho_vendor_id', label: 'Vendor Selection' });

        // Expense account validation
        let expenseAccountValid = false;
        if (purchase.line_items_data) {
            try {
                const lineItems = typeof purchase.line_items_data === 'string'
                    ? JSON.parse(purchase.line_items_data)
                    : purchase.line_items_data;

                if (Array.isArray(lineItems) && lineItems.length > 0) {
                    const allItemsHaveAccounts = lineItems.every(item =>
                        item.expense_account_id && item.expense_account_id.trim() !== ''
                    );

                    if (allItemsHaveAccounts) {
                        expenseAccountValid = true;
                    } else {
                        missingFields.push({ field: 'line_items_accounts', label: 'Line Items Expense Accounts' });
                    }
                } else {
                    if (purchase.zoho_expense_account_id) {
                        expenseAccountValid = true;
                    } else {
                        missingFields.push({ field: 'zoho_expense_account_id', label: 'Expense Account' });
                    }
                }
            } catch (error) {
                if (purchase.zoho_expense_account_id) {
                    expenseAccountValid = true;
                } else {
                    missingFields.push({ field: 'zoho_expense_account_id', label: 'Expense Account' });
                }
            }
        } else {
            if (purchase.zoho_expense_account_id) {
                expenseAccountValid = true;
            } else {
                missingFields.push({ field: 'zoho_expense_account_id', label: 'Expense Account' });
            }
        }

        // SIMPLIFIED TDS validation - only check if tax is selected when TDS is applicable
        if (purchase.tds_applicable) {
            if (!purchase.tds_rate || purchase.tds_rate <= 0) {
                missingFields.push({ field: 'tds_rate', label: 'TDS Rate (when TDS is applicable)' });
            }
            if (!purchase.tds_tax_id || purchase.tds_tax_id.trim() === '') {
                missingFields.push({ field: 'tds_tax_id', label: 'TDS Tax Selection (required when TDS is applicable)' });
            }
        }

        const totalFields = 7;
        const validFields = totalFields - missingFields.length;
        const completionPercentage = Math.round((validFields / totalFields) * 100);

        return {
            isValid: missingFields.length === 0 && !isDuplicate,
            missingFields,
            completionPercentage,
            validFields,
            totalFields,
            isDuplicate,
            expenseAccountValid,
            hasLineItems: !!purchase.line_items_data,
            reverseChargeApplicable: purchase.reverse_charge_applicable || false,
            tdsApplicable: purchase.tds_applicable || false,
            tdsRate: purchase.tds_rate || 0,
            tdsSelected: !!(purchase.tds_tax_id && purchase.tds_tax_id.trim() !== '')
        };
    };

    // Fetch current organization
    const fetchCurrentOrganization = async () => {
        try {
            console.log('🔍 Fetching current organization...');
            const response = await fetch(`${API_BASE_URL}/token/status`, {
                headers: getAuthHeaders()
            });
            const result = await response.json();

            if (result.success && result.data.current_organization) {
                const org = result.data.current_organization;
                console.log('✅ Current organization fetched:', org);
                setCurrentOrganization(org);
                return org.organization_id;
            } else {
                console.error('❌ No current organization found');
                throw new Error('No current organization found');
            }
        } catch (error) {
            console.error('❌ Error fetching current organization:', error);
        }
    };

    // Initialize data
    useEffect(() => {
        const initializeComponent = async () => {
            try {
                await fetchCurrentOrganization();
            } catch (error) {
                console.error('Failed to initialize component:', error);
            }
        };
        initializeComponent();
    }, []);

    useEffect(() => {
        if (currentOrganization?.organization_id) {
            loadAllData();
        }
    }, [currentOrganization?.organization_id]);

    useEffect(() => {
        setPurchases(prev => prev.map(purchase => ({
            ...purchase,
            validationStatus: calculateValidationStatus(purchase, prev)
        })));
    }, [vendors, expenseAccounts]);

    const loadAllData = async () => {
        if (!currentOrganization?.organization_id) {
            console.log('❌ No organization ID available, skipping load');
            return;
        }
        setLoading(true);
        try {
            await Promise.all([
                fetchPurchases(),
                fetchVendors(),
                fetchExpenseAccounts(),
                fetchTdsRates() // FIXED: Fetch TDS rates from local taxes table
            ]);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Add approval status functions
    const canEdit = useCallback((purchase) => {
        const status = purchase.approver_status ||
            purchase.approvalInfo?.status ||
            purchase.approval_status;

        // Only allow editing if status is explicitly 'approved'
        return status === 'approved';
    }, []);

    const canSync = useCallback((purchase) => {
        const status = purchase.approver_status ||
            purchase.approvalInfo?.status ||
            purchase.approval_status;

        // Only allow syncing if status is explicitly 'approved'
        return status === 'approved';
    }, []);

    const getApprovalStatusDisplay = useCallback((purchase) => {
        const status = purchase.approver_status ||
            purchase.approvalInfo?.status ||
            purchase.approval_status;

        console.log('Purchase Approval Status Debug:', {
            id: purchase.id,
            approver_status: purchase.approver_status,
            approvalInfo: purchase.approvalInfo,
            approval_status: purchase.approval_status,
            detected_status: status
        });

        if (status === 'approved') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Approved
                    {purchase.approved_by && (
                        <span className="ml-1">by {purchase.approved_by}</span>
                    )}
                </span>
            );
        }

        if (status === 'pending_approval') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Clock className="w-3 h-3 mr-1" />
                    Pending Approval
                </span>
            );
        }

        if (status === 'rejected') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Rejected
                    {purchase.rejection_reason && (
                        <div className="text-xs mt-1" title={purchase.rejection_reason}>
                            Reason: {purchase.rejection_reason.substring(0, 30)}...
                        </div>
                    )}
                </span>
            );
        }

        return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Status Unknown
            </span>
        );
    }, []);

    // Enhanced Purchase Row Component
    const PurchaseRow = ({
        purchase, vendors, expenseAccounts, isSelected, onToggleSelect,
        onEdit, formatCurrency, getStatusIcon, getStatusText, getCompletionColor,
        isDuplicate, isFutureDate
    }) => {
        const [expanded, setExpanded] = useState(false);

        // Now these functions are available as props
        const canSelect = purchase.validationStatus?.isValid && !purchase.zoho_bill_id && !isDuplicate && canSync(purchase);
        const canEditPurchase = !purchase.zoho_bill_id && !isDuplicate && purchase.zoho_sync_status !== 'synced' && canEdit(purchase);

        const completionPercentage = purchase.validationStatus?.completionPercentage || 0;

        return (
            <>
                <tr className={`border-b hover:bg-gray-50 ${isFutureDate ? 'bg-yellow-50' : ''} ${isDuplicate ? 'bg-orange-50' : ''}`}>
                    {/* Checkbox column */}
                    <td className="p-4">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onToggleSelect(purchase.id)}
                            disabled={!canSelect}
                            className={`rounded ${!canSelect ? 'opacity-50' : ''}`}
                        />
                    </td>

                    {/* Status column with approval status */}
                    <td className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                            {getStatusIcon(purchase, isDuplicate)}
                            <span className="text-xs font-medium">{getStatusText(purchase, isDuplicate)}</span>
                            {isFutureDate && (
                                <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">
                                    Future Date
                                </span>
                            )}
                        </div>

                        {/* Add approval status display */}
                        <div className="mb-2">
                            {getApprovalStatusDisplay(purchase)}
                        </div>

                        <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                                className={`h-2 rounded-full transition-all ${getCompletionColor(completionPercentage)}`}
                                style={{ width: `${completionPercentage}%` }}
                            />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{completionPercentage}% complete</div>
                    </td>

                    <td className="p-4">
                        <div className="font-medium text-gray-900">#{purchase.invoice_no || 'N/A'}</div>
                        <div className="text-sm text-gray-600">{purchase.vendor_name || 'No vendor'}</div>
                        <div className="text-xs text-gray-500">
                            {purchase.date ? new Date(purchase.date).toLocaleDateString() : 'No date'}
                        </div>
                        {purchase.gstin && (
                            <div className="text-xs text-blue-600">GST: {purchase.gstin}</div>
                        )}
                        {purchase.AWS_File_link && (
                            <div className="text-xs">
                                <a
                                    href={purchase.AWS_File_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                    <File className="w-3 h-3" />
                                    View Invoice
                                </a>
                            </div>
                        )}
                    </td>

                    {/* Vendor column with edit restrictions */}
                    <td className="p-4">
                        {purchase.zoho_vendor_id ? (
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <div>
                                    <div className="text-sm font-medium text-green-700">
                                        {purchase.zoho_vendor_name}
                                    </div>
                                    <div className="text-xs text-gray-500">Vendor Selected</div>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => onEdit(purchase)}
                                disabled={!canEditPurchase}
                                className={`text-xs px-2 py-1 rounded ${canEditPurchase
                                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                    : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                    }`}
                                title={
                                    !canEditPurchase && !canEdit(purchase)
                                        ? `Cannot edit: ${purchase.approver_status === 'pending_approval' ? 'pending approval' : purchase.approver_status === 'rejected' ? 'rejected' : 'not approved'}`
                                        : canEditPurchase
                                            ? 'Select Vendor'
                                            : 'Locked'
                                }
                            >
                                {canEditPurchase ? 'Select Vendor' : 'Locked'}
                            </button>
                        )}
                    </td>

                    <td className="p-4">
                        {(() => {
                            // Check if we have line items with account assignments
                            if (purchase.line_items_data) {
                                try {
                                    const lineItems = typeof purchase.line_items_data === 'string'
                                        ? JSON.parse(purchase.line_items_data)
                                        : purchase.line_items_data;

                                    if (Array.isArray(lineItems) && lineItems.length > 0) {
                                        const accountsUsed = [...new Set(lineItems
                                            .filter(item => item.expense_account_name)
                                            .map(item => item.expense_account_name)
                                        )];

                                        if (accountsUsed.length > 0) {
                                            return (
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-1">
                                                        <Receipt className="w-3 h-3 text-green-500" />
                                                        <span className="text-xs font-medium text-green-700">
                                                            {lineItems.length} item(s) mapped
                                                        </span>
                                                    </div>
                                                    {accountsUsed.map((accountName, index) => (
                                                        <div key={index} className="text-xs text-gray-600 bg-blue-50 px-2 py-1 rounded">
                                                            {accountName}
                                                        </div>
                                                    ))}
                                                    {accountsUsed.length > 1 && (
                                                        <div className="text-xs text-blue-600 font-medium">
                                                            Multiple accounts used
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }
                                    }
                                } catch (error) {
                                    console.log('Error parsing line items for display:', error);
                                }
                            }

                            // Fallback to main expense account
                            if (purchase.zoho_expense_account_id) {
                                return (
                                    <div className="flex items-center gap-1">
                                        <Receipt className="w-3 h-3 text-green-500" />
                                        <span className="text-xs text-gray-600">{purchase.zoho_expense_account_name}</span>
                                    </div>
                                );
                            }

                            // No account assigned
                            return (
                                <button
                                    onClick={() => onEdit(purchase)}
                                    disabled={!canEdit}
                                    className={`text-xs px-2 py-1 rounded ${canEdit
                                        ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                        : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                        }`}
                                >
                                    {canEdit ? 'Select Account' : 'Locked'}
                                </button>
                            );
                        })()}
                    </td>

                    <td className="p-4">
                        <div className="font-medium">{formatCurrency(purchase.total)}</div>
                        <div className="text-xs text-gray-500">
                            {purchase.reverse_charge_applicable ? (
                                <span className="bg-orange-100 text-orange-700 px-1 rounded">Reverse Charge</span>
                            ) : (
                                <span className="text-gray-400">Normal Tax</span>
                            )}
                        </div>
                        <div className="text-xs text-gray-500">Period: {purchase.period_of_invoice || 'N/A'}</div>
                    </td>

                    {/* Actions column with edit restrictions */}
                    <td className="p-4">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setExpanded(!expanded)}
                                className="p-1 text-gray-400 hover:text-gray-600"
                            >
                                {expanded ? (
                                    <ChevronUp className="w-4 h-4" />
                                ) : (
                                    <ChevronDown className="w-4 h-4" />
                                )}
                            </button>

                            <button
                                onClick={() => onEdit(purchase)}
                                disabled={!canEditPurchase}
                                className={`p-1 ${canEditPurchase
                                    ? 'text-blue-600 hover:text-blue-800'
                                    : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                title={
                                    !canEditPurchase && !canEdit(purchase)
                                        ? `Cannot edit: ${purchase.approver_status === 'pending_approval' ? 'pending approval' : purchase.approver_status === 'rejected' ? 'rejected' : 'not approved'}`
                                        : canEditPurchase
                                            ? 'Edit purchase'
                                            : 'Cannot edit synced purchase'
                                }
                            >
                                <Edit className="w-4 h-4" />
                            </button>

                            {purchase.zoho_bill_id && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                    Bill: {purchase.zoho_bill_id}
                                </span>
                            )}

                            {isDuplicate && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                                    <Copy className="w-3 h-3 inline mr-1" />
                                    Duplicate
                                </span>
                            )}
                        </div>
                    </td>
                </tr>

                {expanded && (
                    <tr className="bg-gray-50">
                        <td colSpan="7" className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Item Description</h4>
                                    <p className="text-gray-600 bg-white p-2 rounded border">
                                        {purchase.item || 'No description'}
                                    </p>

                                    {/* Payment Terms */}
                                    <h4 className="font-medium text-gray-900 mb-2 mt-4">Payment Terms</h4>
                                    <div className="bg-white p-2 rounded border">
                                        <div className="text-xs text-gray-600">
                                            {purchase.Payment_Terms || 'Not specified'}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Tax Validation</h4>
                                    <div className="bg-white p-2 rounded border">
                                        {purchase.sgst || purchase.cgst || purchase.igst ? (
                                            <div className="space-y-1">
                                                {purchase.igst > 0 ? (
                                                    <div className="text-blue-600">IGST: {purchase.igst}%</div>
                                                ) : (
                                                    <>
                                                        {purchase.sgst > 0 && <div className="text-green-600">SGST: {purchase.sgst}%</div>}
                                                        {purchase.cgst > 0 && <div className="text-green-600">CGST: {purchase.cgst}%</div>}
                                                    </>
                                                )}
                                                <div className="text-xs text-gray-500">
                                                    Tax Amount: ₹{(parseFloat(purchase.total || 0) - parseFloat(purchase.amount || 0)).toFixed(2)}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-gray-500">No tax details</div>
                                        )}
                                    </div>

                                    {/* Validation Status */}
                                    <h4 className="font-medium text-gray-900 mb-2 mt-4">Validation Status</h4>
                                    <div className="bg-white p-2 rounded border">
                                        {purchase.validationStatus?.missingFields?.length > 0 ? (
                                            <div className="space-y-1">
                                                {purchase.validationStatus.missingFields.map((field, index) => (
                                                    <div key={index} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                                        {field.label}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                                                All validations passed
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Sync Status */}
                            {purchase.zoho_sync_status === 'failed' && purchase.zoho_sync_error && (
                                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                                    <div className="font-medium text-red-800 mb-1">Sync Error:</div>
                                    <div className="text-red-700 text-sm">{purchase.zoho_sync_error}</div>
                                    {purchase.zoho_sync_date && (
                                        <div className="text-red-600 text-xs mt-1">
                                            Failed at: {new Date(purchase.zoho_sync_date).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </td>
                    </tr>
                )}
            </>
        );
    };

    // Enhanced Edit Modal with TDS, Reverse Charge, and File Upload
    const EditPurchaseModal = ({
        purchase, vendors, expenseAccounts, tdsRates, onClose, onSave, onCreateVendor, currentOrganization, fetchVendors
    }) => {
        const [formData, setFormData] = useState({
            zoho_vendor_id: purchase.zoho_vendor_id || '',
            zoho_vendor_name: purchase.zoho_vendor_name || '',
            zoho_expense_account_id: purchase.zoho_expense_account_id || '',
            zoho_expense_account_name: purchase.zoho_expense_account_name || '',
            reverse_charge_applicable: purchase.reverse_charge_applicable || false,
            tds_applicable: purchase.tds_applicable || false,
            tds_rate: parseFloat(purchase.tds_rate || 0),
            tds_amount: parseFloat(purchase.tds_amount || 0),
            tds_tax_id: purchase.tds_tax_id || '', // FIXED: Use existing tds_tax_id for correct dropdown selection
            tds_tax_name: purchase.tds_tax_name || '', // FIXED: Use existing tds_tax_name
            discount_amount: parseFloat(purchase.discount_amount || 0),
            discount_percentage: parseFloat(purchase.discount_percentage || 0),
            is_inclusive_tax: purchase.is_inclusive_tax || false,
            payment_terms: purchase.Payment_Terms || '',
            notes: purchase.notes || '',
            bill_date: purchase.bill_date || purchase.date || '',
            due_date: purchase.due_date || '',
            line_items: []
        });

        // Add this state at the top of EditPurchaseModal component
        const [editingVendorData, setEditingVendorData] = useState({
            vendor_name: purchase.vendor_name || '',
            gstin: purchase.gstin || '',
            email: purchase.Email_address || '',
            phone: purchase.Phone || '',
            currency: purchase.currency || 'INR'
        });

        const [activeTab, setActiveTab] = useState('vendor');
        const [searchTerm, setSearchTerm] = useState('');

        // ADD APPROVAL STATUS CHECK
        const canEditPurchase = useCallback(() => {
            const status = purchase?.approver_status ||
                purchase?.approvalInfo?.status ||
                purchase?.approval_status;

            console.log('EditModal - Checking purchase approval status:', {
                purchase_id: purchase?.id,
                approver_status: purchase?.approver_status,
                status: status,
                canEdit: status === 'approved' || !status
            });

            // Only allow editing if approved or no status (defaults to approved)
            return status === 'approved' || !status;
        }, [purchase]);

        // CHECK APPROVAL STATUS BEFORE RENDERING FORM
        if (purchase && !canEditPurchase()) {
            return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-900">Cannot Edit Purchase</h2>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="w-8 h-8 text-yellow-500" />
                            <div>
                                <h3 className="font-medium text-gray-900">Editing Restricted</h3>
                                <p className="text-sm text-gray-600">
                                    This purchase cannot be edited because it is:
                                </p>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                            <div className="flex items-center gap-2">
                                {purchase?.approver_status === 'pending_approval' && (
                                    <>
                                        <Clock className="w-4 h-4 text-yellow-600" />
                                        <span className="text-sm font-medium text-yellow-800">Pending Approval</span>
                                    </>
                                )}
                                {purchase?.approver_status === 'rejected' && (
                                    <>
                                        <XCircle className="w-4 h-4 text-red-600" />
                                        <span className="text-sm font-medium text-red-800">Rejected</span>
                                    </>
                                )}
                            </div>
                            {purchase?.rejection_reason && (
                                <p className="text-xs text-gray-700 mt-2">
                                    <strong>Reason:</strong> {purchase.rejection_reason}
                                </p>
                            )}
                        </div>

                        <div className="text-sm text-gray-600 mb-4">
                            Only approved purchases can be edited. Please contact an approver to change the status if needed.
                        </div>

                        <button
                            onClick={onClose}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            );
        }

        // Add this function inside the EditPurchaseModal component, after the existing handler functions
        const handleDeleteLineItem = (index, itemName) => {
            const isConfirmed = window.confirm(
                `Are you sure you want to delete "${itemName}"?\n\nOnce deleted, this cannot be undone.`
            );

            if (isConfirmed) {
                console.log(`🗑️ Deleting line item ${index + 1}: ${itemName}`);

                setFormData(prev => {
                    const updatedLineItems = prev.line_items.filter((_, i) => i !== index);

                    console.log(`📋 Line items after deletion:`, updatedLineItems);

                    // If no items left, create a default item
                    if (updatedLineItems.length === 0) {
                        const sgstRate = parseFloat(purchase.sgst || 0);
                        const cgstRate = parseFloat(purchase.cgst || 0);
                        const igstRate = parseFloat(purchase.igst || 0);
                        const totalAmount = parseFloat(purchase.amount || 0);

                        const sgstAmount = (totalAmount * sgstRate) / 100;
                        const cgstAmount = (totalAmount * cgstRate) / 100;
                        const igstAmount = (totalAmount * igstRate) / 100;
                        const totalTax = sgstAmount + cgstAmount + igstAmount;

                        const defaultItem = {
                            name: purchase.item || 'Purchase Item',
                            description: purchase.item || 'Purchase Item',
                            amount: totalAmount,
                            quantity: 1,
                            rate: totalAmount,
                            sgst_rate: sgstRate,
                            cgst_rate: cgstRate,
                            igst_rate: igstRate,
                            sgst_amount: sgstAmount,
                            cgst_amount: cgstAmount,
                            igst_amount: igstAmount,
                            total_tax: totalTax,
                            total_with_tax: totalAmount + totalTax,
                            expense_account_id: '',
                            expense_account_name: '',
                            tds_applicable: false,
                            tds_rate: 0,
                            tds_amount: 0,
                            tds_tax_id: '',
                            tds_tax_name: '',
                            itc_eligibility: ''
                        };

                        console.log('📋 Created default item after deleting all items');
                        return {
                            ...prev,
                            line_items: [defaultItem]
                        };
                    }

                    return {
                        ...prev,
                        line_items: updatedLineItems
                    };
                });
            }
        };

        // AI Expense Account Suggestion
        const suggestExpenseAccount = (itemDescription) => {
            const description = itemDescription.toLowerCase();

            // Enhanced expense account mappings with multiple keywords
            const accountMappings = [
                {
                    keywords: ['rent', 'office rent', 'space', 'lease', 'premises'],
                    suggestion: 'Rent'
                },
                {
                    keywords: ['travel', 'taxi', 'uber', 'flight', 'hotel', 'accommodation', 'transport'],
                    suggestion: 'Travel'
                },
                {
                    keywords: ['telephone', 'phone', 'mobile', 'internet', 'wifi', 'broadband', 'telecom'],
                    suggestion: 'Telephone'
                },
                {
                    keywords: ['food', 'meal', 'lunch', 'dinner', 'restaurant', 'catering', 'snacks'],
                    suggestion: 'Meals'
                },
                {
                    keywords: ['stationery', 'paper', 'pen', 'office supplies', 'supplies', 'printing'],
                    suggestion: 'Office Supplies'
                },
                {
                    keywords: ['software', 'license', 'subscription', 'saas', 'application', 'tool'],
                    suggestion: 'Software'
                },
                {
                    keywords: ['electricity', 'power', 'water', 'utilities', 'electric', 'gas'],
                    suggestion: 'Utilities'
                },
                {
                    keywords: ['maintenance', 'repair', 'service', 'fixing', 'upkeep'],
                    suggestion: 'Repairs'
                },
                {
                    keywords: ['advertisement', 'marketing', 'promotion', 'advertising', 'campaign'],
                    suggestion: 'Advertising'
                },
                {
                    keywords: ['fuel', 'petrol', 'diesel', 'gas', 'gasoline'],
                    suggestion: 'Fuel'
                }
            ];

            console.log('🔍 AI analyzing description:', description);

            for (const mapping of accountMappings) {
                const hasKeyword = mapping.keywords.some(keyword => description.includes(keyword));
                if (hasKeyword) {
                    console.log('📍 Found keyword match for:', mapping.suggestion);

                    // More flexible matching - look for partial matches
                    const matchingAccount = expenseAccounts.find(account => {
                        const accountName = account.account_name.toLowerCase();
                        return accountName.includes(mapping.suggestion.toLowerCase()) ||
                            mapping.suggestion.toLowerCase().includes(accountName.split(' ')[0]) ||
                            mapping.keywords.some(keyword => accountName.includes(keyword));
                    });

                    if (matchingAccount) {
                        console.log('✅ AI found matching account:', matchingAccount.account_name);
                        return matchingAccount;
                    } else {
                        console.log('❌ No matching account found for suggestion:', mapping.suggestion);
                    }
                }
            }

            console.log('❌ No AI suggestion found for description:', description);
            return null;
        };

        // AI ITC Suggestion
        const suggestITC = (itemDescription, amount) => {
            const description = itemDescription.toLowerCase();
            const amountValue = parseFloat(amount) || 0;

            // ITC ineligible items as per GST rules
            const ineligibleKeywords = [
                'food', 'beverage', 'outdoor catering', 'beauty treatment', 'health service',
                'cosmetic', 'plastic surgery', 'membership club', 'health club', 'fitness',
                'life insurance', 'health insurance', 'motor vehicle', 'construction',
                'works contract', 'rent'
            ];

            // Section 17(5) specific items
            const section17Keywords = [
                'motor vehicle', 'vessel', 'aircraft', 'construction', 'building',
                'civil structure', 'rent', 'telephone', 'internet', 'employee welfare',
                'food', 'beverage', 'outdoor catering', 'beauty treatment', 'health service',
                'cosmetic', 'plastic surgery', 'membership club', 'health club', 'fitness',
                'life insurance', 'health insurance', 'motor vehicle', 'construction',
                'works contract', 'rent'
            ];

            // Check for section 17(5) items
            if (section17Keywords.some(keyword => description.includes(keyword))) {
                return 'ineligible_section17';
            }

            // Check for other ineligible items
            if (ineligibleKeywords.some(keyword => description.includes(keyword))) {
                return 'ineligible_others';
            }

            // Items above certain threshold might need scrutiny
            if (amountValue > 50000) {
                return 'eligible'; // But flag for review
            }

            return 'eligible';
        };

        const getAISuggestion = async (itemDescription, vendorName = null) => {
            try {
                console.log('🤖 Requesting OpenAI suggestion for:', itemDescription, 'from vendor:', vendorName);

                const response = await fetch(`${API_BASE_URL}/purchases/ai-suggest-expense-account`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        item_description: itemDescription,
                        vendor_name: vendorName || purchase.vendor_name,
                        company_id: currentOrganization.organization_id
                    })
                });

                const data = await response.json();

                if (data.success && data.suggestion) {
                    // Frontend validation against generic accounts
                    const genericAccountNames = [
                        'purchase - services',
                        'business development',
                        'partnership development',
                        'sundry expenses',
                        'general expenses',
                        'miscellaneous expenses',
                        'other expenses',
                        'uncategorized expenses'
                    ];

                    const suggestedName = data.suggestion.expense_account.account_name.toLowerCase();
                    const isGeneric = genericAccountNames.some(generic =>
                        suggestedName.includes(generic) || suggestedName === generic
                    );

                    if (isGeneric) {
                        console.log('❌ AI suggested generic account, rejecting:', data.suggestion.expense_account.account_name);
                        return null;
                    }

                    console.log('✅ OpenAI suggestion accepted:', data.suggestion);
                    return {
                        account_id: data.suggestion.expense_account.account_id,
                        account_name: data.suggestion.expense_account.account_name,
                        suggestion: data.suggestion
                    };
                } else {
                    console.log('❌ No OpenAI suggestion available:', data.message);
                    return null;
                }
            } catch (error) {
                console.error('❌ Error getting OpenAI suggestion:', error);
                return null;
            }
        };

        // Bulk apply expense account to all items
        const handleBulkExpenseAccountApplication = (account) => {
            setFormData(prev => ({
                ...prev,
                zoho_expense_account_id: account.account_id,
                zoho_expense_account_name: account.account_name,
                line_items: prev.line_items.map(item => ({
                    ...item,
                    expense_account_id: account.account_id,
                    expense_account_name: account.account_name
                }))
            }));
        };

        // Bulk apply ITC to all items
        const handleBulkITCApplication = (itcValue) => {
            setFormData(prev => ({
                ...prev,
                line_items: prev.line_items.map(item => ({
                    ...item,
                    itc_eligibility: itcValue
                }))
            }));
        };

        // AI suggestion for all line items
        const applyAISuggestionsToAll = async () => {
            console.log('🤖 Applying AI suggestions to all line items...');

            const updatedLineItems = [...formData.line_items];

            for (let index = 0; index < updatedLineItems.length; index++) {
                const item = updatedLineItems[index];
                const suggestion = await getAISuggestion(item.description || item.name);

                if (suggestion) {
                    updatedLineItems[index] = {
                        ...item,
                        expense_account_id: suggestion.expense_account.account_id,
                        expense_account_name: suggestion.expense_account.account_name,
                        itc_eligibility: suggestion.itc_eligibility,
                        tds_applicable: suggestion.tds_applicable,
                        tds_rate: suggestion.tds_details?.rate || 0,
                        tds_amount: suggestion.tds_details?.rate ?
                            ((item.amount || 0) * suggestion.tds_details.rate) / 100 : 0,
                        tds_tax_id: suggestion.tds_details?.tax_id || '',
                        tds_tax_name: suggestion.tds_details?.tax_name || '',
                        ai_suggested: true
                    };

                    // Apply reverse charge globally if any item suggests it
                    if (suggestion.reverse_charge_applicable) {
                        setFormData(prev => ({
                            ...prev,
                            reverse_charge_applicable: true
                        }));
                    }
                }
            }

            setFormData(prev => ({
                ...prev,
                line_items: updatedLineItems
            }));

            console.log('✅ AI suggestions applied to all items');
        };

        const tabs = [
            { id: 'vendor', name: 'Vendor & Account', icon: Building },
            { id: 'items', name: 'Line Items & Accounts', icon: Receipt },
            // { id: 'tax', name: 'Tax & TDS', icon: Calculator },
            { id: 'terms', name: 'Terms & Notes', icon: FileText }
        ];

        const parseLineItems = (itemDescription, totalAmount, sgstRate, cgstRate, igstRate) => {
            console.log('🔍 Parsing line items from:', { itemDescription, totalAmount, sgstRate, cgstRate, igstRate });

            if (!itemDescription) return [];

            // Enhanced regex to handle various formats
            const itemRegex = /([^:,]+?):\s*₹([0-9,]+\.?\d*)/g;
            const items = [];
            let match;
            let totalParsedAmount = 0;

            while ((match = itemRegex.exec(itemDescription)) !== null) {
                const itemName = match[1].trim();
                const itemPrice = parseFloat(match[2].replace(/,/g, ''));

                if (itemName && itemPrice > 0) {
                    const sgstAmount = (itemPrice * sgstRate) / 100;
                    const cgstAmount = (itemPrice * cgstRate) / 100;
                    const igstAmount = (itemPrice * igstRate) / 100;
                    const totalTax = sgstAmount + cgstAmount + igstAmount;

                    items.push({
                        name: itemName,
                        description: itemName,
                        amount: itemPrice,
                        quantity: 1,
                        rate: itemPrice,
                        sgst_rate: sgstRate,
                        cgst_rate: cgstRate,
                        igst_rate: igstRate,
                        sgst_amount: sgstAmount,
                        cgst_amount: cgstAmount,
                        igst_amount: igstAmount,
                        total_tax: totalTax,
                        total_with_tax: itemPrice + totalTax,
                        expense_account_id: '',
                        expense_account_name: '',
                        tds_applicable: false,
                        tds_rate: 0,
                        tds_amount: 0,
                        tds_tax_id: '',
                        tds_tax_name: '',
                        itc_eligibility: ''
                    });

                    totalParsedAmount += itemPrice;
                }
            }

            // If no items found or amounts don't match, create single item
            const expectedAmount = parseFloat(totalAmount || 0);
            if (items.length === 0 || Math.abs(totalParsedAmount - expectedAmount) > 1) {
                const sgstAmount = (expectedAmount * sgstRate) / 100;
                const cgstAmount = (expectedAmount * cgstRate) / 100;
                const igstAmount = (expectedAmount * igstRate) / 100;
                const totalTax = sgstAmount + cgstAmount + igstAmount;

                const singleItem = {
                    name: itemDescription || 'Purchase Item',
                    description: itemDescription || 'Purchase Item',
                    amount: expectedAmount,
                    quantity: 1,
                    rate: expectedAmount,
                    sgst_rate: sgstRate,
                    cgst_rate: cgstRate,
                    igst_rate: igstRate,
                    sgst_amount: sgstAmount,
                    cgst_amount: cgstAmount,
                    igst_amount: igstAmount,
                    total_tax: totalTax,
                    total_with_tax: expectedAmount + totalTax,
                    expense_account_id: '',
                    expense_account_name: '',
                    tds_applicable: false,
                    tds_rate: 0,
                    tds_amount: 0,
                    tds_tax_id: '',
                    tds_tax_name: '',
                    itc_eligibility: ''
                };

                console.log('📋 Created single line item:', singleItem);
                return [singleItem];
            }

            console.log('📋 Parsed multiple line items:', items);
            return items;
        };

        const calculateDueDate = (billDate, paymentTerm) => {
            if (!billDate || !paymentTerm) return '';

            // Create date in local timezone to avoid UTC shift issues
            const dateParts = billDate.split('-');
            const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));

            // Handle "Due on Receipt" - should be same as bill date (0 days)
            if (paymentTerm.toLowerCase().includes('due on receipt') || paymentTerm.toLowerCase().includes('receipt')) {
                // Return the same date as bill date for "Due on Receipt"
                return billDate;
            } else if (paymentTerm.includes('15')) {
                date.setDate(date.getDate() + 15);
            } else if (paymentTerm.includes('30')) {
                date.setDate(date.getDate() + 30);
            } else if (paymentTerm.includes('45')) {
                date.setDate(date.getDate() + 45);
            } else if (paymentTerm.includes('60')) {
                date.setDate(date.getDate() + 60);
            } else if (paymentTerm.includes('90')) {
                date.setDate(date.getDate() + 90);
            } else {
                date.setDate(date.getDate() + 30); // Default to 30 days
            }

            // Format date manually to avoid timezone issues
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        // Initialize line items from existing data or description
        useEffect(() => {
            console.log('🔄 Loading line items for purchase:', purchase.id);
            console.log('📋 Existing line_items_data:', purchase.line_items_data);

            const sgstRate = parseFloat(purchase.sgst || 0);
            const cgstRate = parseFloat(purchase.cgst || 0);
            const igstRate = parseFloat(purchase.igst || 0);

            // Parse line items from existing data or description
            let parsedItems = [];

            // First try to load from database JSON if available
            if (purchase.line_items_data) {
                try {
                    const existingItems = typeof purchase.line_items_data === 'string'
                        ? JSON.parse(purchase.line_items_data)
                        : purchase.line_items_data;
                    if (Array.isArray(existingItems)) {
                        parsedItems = existingItems;
                        console.log('✅ Loaded existing line items from database:', parsedItems);
                    }
                } catch (error) {
                    console.log('❌ Error parsing existing line items:', error);
                }
            }

            // If no existing data, parse from item description
            if (parsedItems.length === 0) {
                console.log('🔍 No existing line items, parsing from description...');
                parsedItems = parseLineItems(purchase.item, purchase.amount, sgstRate, cgstRate, igstRate);
            }

            // Ensure parsedItems is always an array
            if (!Array.isArray(parsedItems)) {
                console.log('⚠️ parsedItems is not an array, defaulting to empty array');
                parsedItems = [];
            }

            const finalItems = parsedItems.map(item => ({
                ...item,
                expense_account_id: item.expense_account_id || purchase.zoho_expense_account_id || '',
                expense_account_name: item.expense_account_name || purchase.zoho_expense_account_name || ''
            }));

            console.log('📋 Final line items being set:', finalItems);

            setFormData(prev => ({
                ...prev,
                line_items: finalItems,
                bill_date: formatDateForInput(purchase.bill_date || purchase.date),
                due_date: formatDateForInput(purchase.due_date)
            }));
        }, [purchase]);

        // Auto-calculate due date when bill date or payment terms change
        useEffect(() => {
            if (formData.bill_date && formData.payment_terms) {
                const newDueDate = calculateDueDate(formData.bill_date, formData.payment_terms);
                setFormData(prev => ({ ...prev, due_date: newDueDate }));
            }
        }, [formData.bill_date, formData.payment_terms]);

        const handleTaxRateChange = (itemIndex, rateType, value) => {
            const rate = parseFloat(value) || 0;

            setFormData(prev => ({
                ...prev,
                line_items: prev.line_items.map((item, index) => {
                    if (index !== itemIndex) return item;

                    const updatedItem = { ...item, [rateType]: rate };

                    // Recalculate tax amounts
                    const sgstAmount = (updatedItem.amount * (updatedItem.sgst_rate || 0)) / 100;
                    const cgstAmount = (updatedItem.amount * (updatedItem.cgst_rate || 0)) / 100;
                    const igstAmount = (updatedItem.amount * (updatedItem.igst_rate || 0)) / 100;
                    const totalTax = sgstAmount + cgstAmount + igstAmount;

                    return {
                        ...updatedItem,
                        sgst_amount: sgstAmount,
                        cgst_amount: cgstAmount,
                        igst_amount: igstAmount,
                        total_tax: totalTax,
                        total_with_tax: updatedItem.amount + totalTax
                    };
                })
            }));
        };

        const handleVendorSelect = (vendor) => {
            setFormData(prev => ({
                ...prev,
                zoho_vendor_id: vendor.contact_id,
                zoho_vendor_name: vendor.contact_name
            }));
        };

        const handleAccountSelect = (account) => {
            setFormData(prev => ({
                ...prev,
                zoho_expense_account_id: account.account_id,
                zoho_expense_account_name: account.account_name
            }));
        };

        const handleLineItemAccountSelect = async (itemIndex, account, isAISuggestion = false) => {
            console.log('🔧 Applying account to item with auto-suggestions:', account);

            let updatedItem = {
                expense_account_id: account.account_id,
                expense_account_name: account.account_name
            };

            // If it's an AI suggestion, apply all the AI recommendations
            if (isAISuggestion && account.suggestion) {
                const suggestion = account.suggestion;
                updatedItem = {
                    ...updatedItem,
                    itc_eligibility: suggestion.itc_eligibility,
                    tds_applicable: suggestion.tds_applicable,
                    tds_rate: suggestion.tds_details?.rate || 0,
                    tds_amount: suggestion.tds_details?.rate ?
                        ((formData.line_items[itemIndex]?.amount || 0) * suggestion.tds_details.rate) / 100 : 0,
                    tds_tax_id: suggestion.tds_details?.tax_id || '',
                    tds_tax_name: suggestion.tds_details?.tax_name || ''
                };

                // Apply reverse charge globally if suggested
                if (suggestion.reverse_charge_applicable) {
                    setFormData(prev => ({
                        ...prev,
                        reverse_charge_applicable: true
                    }));
                }
            } else {
                // Manual account selection - look up account properties and auto-suggest
                console.log('🔍 Getting account-based suggestions for manual selection...');
                const accountSuggestions = await getAccountBasedSuggestions(account, formData.line_items[itemIndex]);

                if (accountSuggestions && Object.keys(accountSuggestions).length > 0) {
                    console.log('✅ Applying account suggestions with TDS:', accountSuggestions);
                    updatedItem = {
                        ...updatedItem,
                        itc_eligibility: accountSuggestions.itc_eligibility || 'eligible',
                        tds_applicable: Boolean(accountSuggestions.tds_applicable),
                        tds_rate: parseFloat(accountSuggestions.tds_rate || 0),
                        tds_amount: parseFloat(accountSuggestions.tds_amount || 0),
                        tds_tax_id: accountSuggestions.tds_tax_id || '',
                        tds_tax_name: accountSuggestions.tds_tax_name || ''
                    };

                    // Apply reverse charge globally if suggested
                    if (accountSuggestions.reverse_charge_applicable) {
                        setFormData(prev => ({
                            ...prev,
                            reverse_charge_applicable: true
                        }));
                    }
                } else {
                    console.log('❌ No account suggestions available, using defaults');
                }
            }

            setFormData(prev => ({
                ...prev,
                line_items: prev.line_items.map((item, index) =>
                    index === itemIndex ? { ...item, ...updatedItem } : item
                )
            }));
        };

        // Updated getAccountBasedSuggestions function in frontend
        const getAccountBasedSuggestions = async (account, lineItem) => {
            try {
                console.log('🔍 Getting account-based suggestions for:', account.account_name);

                // Make API call to get account properties from your chart_of_accounts table
                const response = await fetch(`${API_BASE_URL}/purchases/account-suggestions`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        account_id: account.account_id,
                        account_name: account.account_name,
                        item_description: lineItem.description || lineItem.name,
                        item_amount: lineItem.amount,
                        company_id: currentOrganization.organization_id
                    })
                });

                const data = await response.json();

                if (data.success && data.suggestions) {
                    console.log('✅ Got account-based suggestions with TDS validation:', data.suggestions);
                    return data.suggestions;
                } else {
                    console.log('❌ No account suggestions available');
                    return {};
                }
            } catch (error) {
                console.error('❌ Error getting account suggestions:', error);
                return {};
            }
        };

        const handleApplyAccountToAll = (account) => {
            console.log('🔧 Applying account to all items:', account);
            console.log('📋 Current line items before applying to all:', formData.line_items);

            setFormData(prev => {
                const updatedLineItems = (prev.line_items || []).map(item => ({
                    ...item,
                    expense_account_id: account.account_id,
                    expense_account_name: account.account_name
                }));

                console.log('📋 Updated line items after applying to all:', updatedLineItems);

                return {
                    ...prev,
                    zoho_expense_account_id: account.account_id,
                    zoho_expense_account_name: account.account_name,
                    line_items: updatedLineItems
                };
            });
        };

        const filteredVendors = vendors.filter(vendor =>
            vendor.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (vendor.company_name && vendor.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        const paymentTermsOptions = [
            'Due on Receipt',
            'Net 15',
            'Net 30',
            'Net 45',
            'Net 60',
            'Net 90',
            'End of Month',
            'Next Month End',
            '15th of Next Month'
        ];

        // FIXED: Handle TDS selection by tax_id instead of rate to avoid conflicts
        const handleTdsRateChange = (selectedValue) => {
            console.log('🔧 TDS rate change - Selected value:', selectedValue);

            // Parse the selected value - it should be tax_id now
            const selectedTdsTax = tdsRates.find(tds => tds.tax_id === selectedValue);
            console.log('🔧 Found TDS tax:', selectedTdsTax);

            if (selectedTdsTax) {
                const numericRate = parseFloat(selectedTdsTax.value) || 0;
                const amount = (parseFloat(purchase.amount || 0) * numericRate) / 100;

                console.log('🔧 Setting TDS data:', {
                    rate: numericRate,
                    amount: amount,
                    tax_id: selectedTdsTax.tax_id,
                    tax_name: selectedTdsTax.tax_name
                });

                setFormData(prev => ({
                    ...prev,
                    tds_rate: numericRate,
                    tds_amount: amount,
                    tds_applicable: numericRate > 0,
                    tds_tax_id: selectedTdsTax.tax_id || '', // FIXED: Ensure this is set
                    tds_tax_name: selectedTdsTax.tax_name || '' // FIXED: Ensure this is set
                }));
            } else {
                // Reset TDS if nothing selected
                setFormData(prev => ({
                    ...prev,
                    tds_rate: 0,
                    tds_amount: 0,
                    tds_applicable: false,
                    tds_tax_id: '',
                    tds_tax_name: ''
                }));
            }
        };

        // Vendor tab content
        const renderVendorTab = () => (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Vendor Selection */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h4 className="font-medium text-gray-900">Vendor Selection</h4>
                        <div className="flex gap-2">
                            {/* Auto-Create Vendor Button */}
                            {!formData.zoho_vendor_id && purchase.vendor_name && (
                                <button
                                    onClick={async () => {
                                        try {
                                            console.log('🤖 Auto-creating vendor for:', purchase.vendor_name);

                                            // Call auto-process for single purchase
                                            const response = await fetch(`${API_BASE_URL}/purchases/auto-process?company_id=${currentOrganization.organization_id}`, {
                                                method: 'POST',
                                                headers: getAuthHeaders(),
                                                body: JSON.stringify({
                                                    purchase_ids: [purchase.id]
                                                })
                                            });

                                            const data = await response.json();

                                            if (data.success && data.results[0]?.success) {
                                                const result = data.results[0];

                                                // Update form data with created vendor
                                                setFormData(prev => ({
                                                    ...prev,
                                                    zoho_vendor_id: result.vendor_id,
                                                    zoho_vendor_name: result.vendor_name || purchase.vendor_name
                                                }));

                                                // Refresh vendors list if function is provided
                                                if (fetchVendors && typeof fetchVendors === 'function') {
                                                    await fetchVendors();
                                                }

                                                alert(`✅ Vendor ${result.vendor_action === 'created_new' ? 'created' : 'found'} successfully!`);
                                            } else {
                                                throw new Error(data.message || 'Auto-processing failed');
                                            }
                                        } catch (error) {
                                            console.error('❌ Auto-create vendor error:', error);
                                            alert('Failed to auto-create vendor: ' + error.message);
                                        }
                                    }}
                                    className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200"
                                >
                                    <Brain className="w-3 h-3 inline mr-1" />
                                    Auto-Create
                                </button>
                            )}

                            <button
                                onClick={() => onCreateVendor()}
                                className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                            >
                                <Plus className="w-3 h-3 inline mr-1" />
                                New Vendor
                            </button>
                        </div>
                    </div>

                    {/* Show current purchase vendor info */}
                    {purchase.vendor_name && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                            <div className="flex justify-between items-center mb-2">
                                <div className="text-sm font-medium text-yellow-800">Purchase Vendor Details:</div>
                                <button
                                    onClick={() => setEditingVendorData({
                                        vendor_name: purchase.vendor_name || '',
                                        gstin: purchase.gstin || '',
                                        email: purchase.Email_address || '',
                                        phone: purchase.Phone || '',
                                        currency: purchase.currency || 'INR'
                                    })}
                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                                >
                                    <Edit className="w-3 h-3 inline mr-1" />
                                    Edit Vendor Details
                                </button>
                            </div>

                            {/* Editable fields */}
                            <div className="space-y-2 text-xs">
                                <div>
                                    <label className="text-yellow-700 font-medium">Name:</label>
                                    <input
                                        type="text"
                                        value={editingVendorData.vendor_name}
                                        onChange={(e) => setEditingVendorData(prev => ({ ...prev, vendor_name: e.target.value }))}
                                        className="ml-2 px-2 py-1 border rounded text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="text-yellow-700 font-medium">GSTIN:</label>
                                    <input
                                        type="text"
                                        value={editingVendorData.gstin}
                                        onChange={(e) => setEditingVendorData(prev => ({ ...prev, gstin: e.target.value }))}
                                        className="ml-2 px-2 py-1 border rounded text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="text-yellow-700 font-medium">Email:</label>
                                    <input
                                        type="email"
                                        value={editingVendorData.email}
                                        onChange={(e) => setEditingVendorData(prev => ({ ...prev, email: e.target.value }))}
                                        className="ml-2 px-2 py-1 border rounded text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="text-yellow-700 font-medium">Phone:</label>
                                    <input
                                        type="text"
                                        value={editingVendorData.phone}
                                        onChange={(e) => setEditingVendorData(prev => ({ ...prev, phone: e.target.value }))}
                                        className="ml-2 px-2 py-1 border rounded text-xs"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={async () => {
                                    // Update purchase object
                                    const updatedPurchase = {
                                        ...purchase,
                                        vendor_name: editingVendorData.vendor_name,
                                        gstin: editingVendorData.gstin,
                                        Email_address: editingVendorData.email,
                                        Phone: editingVendorData.phone,
                                        currency: editingVendorData.currency
                                    };

                                    // Call auto-create with updated data
                                    try {
                                        const response = await fetch(`${API_BASE_URL}/purchases/auto-process?company_id=${currentOrganization.organization_id}`, {
                                            method: 'POST',
                                            headers: getAuthHeaders(),
                                            body: JSON.stringify({
                                                purchase_ids: [purchase.id],
                                                vendor_override: editingVendorData
                                            })
                                        });

                                        const data = await response.json();
                                        if (data.success) {
                                            setFormData(prev => ({
                                                ...prev,
                                                zoho_vendor_id: data.results[0].vendor_id,
                                                zoho_vendor_name: data.results[0].vendor_name
                                            }));

                                            if (fetchVendors) await fetchVendors();
                                            alert('Vendor created/updated successfully!');
                                        }
                                    } catch (error) {
                                        alert('Failed to create/update vendor: ' + error.message);
                                    }
                                }}
                                className="mt-2 text-xs bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200"
                            >
                                Update & Auto-Create Vendor
                            </button>
                        </div>
                    )}

                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search vendors..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border rounded-md text-sm"
                        />
                    </div>

                    <div className="max-h-48 overflow-y-auto border rounded-md">
                        {filteredVendors.map((vendor) => (
                            <div
                                key={vendor.contact_id}
                                onClick={() => handleVendorSelect(vendor)}
                                className={`p-3 cursor-pointer hover:bg-gray-50 border-b ${formData.zoho_vendor_id === vendor.contact_id ? 'bg-blue-50 border-blue-200' : ''
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Building className="w-4 h-4 text-gray-400" />
                                    <div>
                                        <div className="font-medium text-sm">{vendor.contact_name}</div>
                                        {vendor.company_name && (
                                            <div className="text-xs text-gray-500">{vendor.company_name}</div>
                                        )}
                                        {vendor.gst_no && (
                                            <div className="text-xs text-blue-600">GST: {vendor.gst_no}</div>
                                        )}
                                    </div>
                                    {formData.zoho_vendor_id === vendor.contact_id && (
                                        <Check className="w-4 h-4 text-blue-600 ml-auto" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredVendors.length === 0 && searchTerm && (
                        <div className="text-center p-4 text-gray-500">
                            <div className="text-sm">No vendors found matching "{searchTerm}"</div>
                            <div className="text-xs mt-1">Try the Auto-Create button to create a vendor automatically</div>
                        </div>
                    )}
                </div>

                {/* Default Expense Account Selection */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h4 className="font-medium text-gray-900">Default Expense Account</h4>
                        <button
                            onClick={() => {
                                const selectedAccount = expenseAccounts.find(a => a.account_id === formData.zoho_expense_account_id);
                                if (selectedAccount) {
                                    handleApplyAccountToAll(selectedAccount);
                                }
                            }}
                            disabled={!formData.zoho_expense_account_id}
                            className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 disabled:opacity-50"
                        >
                            Apply to All Items
                        </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto border rounded-md">
                        {expenseAccounts.map((account) => (
                            <div
                                key={account.account_id}
                                onClick={() => handleAccountSelect(account)}
                                className={`p-3 cursor-pointer hover:bg-gray-50 border-b ${formData.zoho_expense_account_id === account.account_id ? 'bg-blue-50 border-blue-200' : ''
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Receipt className="w-4 h-4 text-purple-500" />
                                    <div>
                                        <div className="font-medium text-sm">{account.account_name}</div>
                                        <div className="text-xs text-gray-500">{account.account_code}</div>
                                    </div>
                                    {formData.zoho_expense_account_id === account.account_id && (
                                        <Check className="w-4 h-4 text-blue-600 ml-auto" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );

        const renderItemsTab = () => (
            <div className="space-y-6">
                {/* Tax Settings moved to top */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-3">Global Tax Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.reverse_charge_applicable}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    reverse_charge_applicable: e.target.checked
                                }))}
                                className="rounded"
                            />
                            <span className="text-sm">Reverse charge applicable</span>
                        </label>

                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.is_inclusive_tax}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    is_inclusive_tax: e.target.checked
                                }))}
                                className="rounded"
                            />
                            <span className="text-sm">Tax inclusive pricing</span>
                        </label>
                    </div>
                </div>

                <div className="space-y-4">
                    {formData.line_items.map((item, index) => {
                        const suggestedAccount = suggestExpenseAccount(item.description || item.name);
                        const suggestedITC = suggestITC(item.description || item.name, item.amount);

                        return (
                            <div key={index} className="border rounded-lg p-4 bg-gray-50">
                                <div className="flex justify-between items-center mb-3">
                                    <h5 className="font-medium text-gray-900">{item.name || item.description}</h5>
                                    {/* Replace the existing delete button in the line item section */}
                                    <div className="flex justify-between items-center mb-3">
                                        <button
                                            onClick={() => handleDeleteLineItem(index, item.name || item.description)}
                                            className="text-red-600 hover:text-red-800 p-1"
                                            title="Delete line item"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                                    {/* Item Details */}
                                    <div className="space-y-2">
                                        <h6 className="text-sm font-medium text-gray-700">Item Details</h6>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div>
                                                <label className="text-gray-600 text-xs">Amount:</label>
                                                <input
                                                    type="number"
                                                    value={item.amount}
                                                    onChange={(e) => {
                                                        const newAmount = parseFloat(e.target.value) || 0;
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            line_items: prev.line_items.map((lineItem, idx) =>
                                                                idx === index ? {
                                                                    ...lineItem,
                                                                    amount: newAmount,
                                                                    rate: newAmount / (lineItem.quantity || 1),
                                                                    total_with_tax: newAmount + lineItem.total_tax
                                                                } : lineItem
                                                            )
                                                        }));
                                                    }}
                                                    className="w-full border rounded px-2 py-1 text-xs"
                                                    step="0.01"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-gray-600 text-xs">Quantity:</label>
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => {
                                                        const newQty = parseFloat(e.target.value) || 1;
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            line_items: prev.line_items.map((lineItem, idx) =>
                                                                idx === index ? {
                                                                    ...lineItem,
                                                                    quantity: newQty,
                                                                    amount: lineItem.rate * newQty,
                                                                    total_with_tax: (lineItem.rate * newQty) + lineItem.total_tax
                                                                } : lineItem
                                                            )
                                                        }));
                                                    }}
                                                    className="w-full border rounded px-2 py-1 text-xs"
                                                    step="0.01"
                                                    min="1"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tax Rates */}
                                    <div className="space-y-2">
                                        <h6 className="text-sm font-medium text-gray-700">Tax Rates</h6>
                                        <div className="grid grid-cols-1 gap-2">
                                            <div>
                                                <label className="text-gray-600 text-xs">SGST (%):</label>
                                                <input
                                                    type="number"
                                                    value={item.sgst_rate}
                                                    onChange={(e) => handleTaxRateChange(index, 'sgst_rate', e.target.value)}
                                                    className="w-full border rounded px-2 py-1 text-xs"
                                                    step="0.01"
                                                    min="0"
                                                    max="50"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-gray-600 text-xs">CGST (%):</label>
                                                <input
                                                    type="number"
                                                    value={item.cgst_rate}
                                                    onChange={(e) => handleTaxRateChange(index, 'cgst_rate', e.target.value)}
                                                    className="w-full border rounded px-2 py-1 text-xs"
                                                    step="0.01"
                                                    min="0"
                                                    max="50"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-gray-600 text-xs">IGST (%):</label>
                                                <input
                                                    type="number"
                                                    value={item.igst_rate}
                                                    onChange={(e) => handleTaxRateChange(index, 'igst_rate', e.target.value)}
                                                    className="w-full border rounded px-2 py-1 text-xs"
                                                    step="0.01"
                                                    min="0"
                                                    max="50"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expense Account */}
                                    <div className="space-y-2">
                                        {/* Replace the existing AI suggestion button section with this */}
                                        <div className="flex items-center justify-between">
                                            <h6 className="text-sm font-medium text-gray-700">Expense Account</h6>
                                            <button
                                                onClick={async () => {
                                                    console.log('🤖 Getting AI suggestion for:', item.description || item.name);

                                                    const suggestion = await getAISuggestion(
                                                        item.description || item.name,
                                                        purchase.vendor_name
                                                    );

                                                    if (suggestion) {
                                                        console.log('✅ Applying AI suggestion:', suggestion);
                                                        await handleLineItemAccountSelect(index, suggestion, true);
                                                    } else {
                                                        alert('No OpenAI suggestion available for this item');
                                                    }
                                                }}
                                                className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 flex items-center gap-1"
                                            >
                                                <Brain className="w-3 h-3" />
                                                AI Suggest
                                            </button>
                                        </div>

                                        <select
                                            value={item.expense_account_id || ''}
                                            onChange={(e) => {
                                                const selectedAccount = expenseAccounts.find(a => a.account_id === e.target.value);
                                                if (selectedAccount) {
                                                    handleLineItemAccountSelect(index, selectedAccount);
                                                }
                                            }}
                                            className="w-full border rounded-md px-2 py-1 text-xs"
                                        >
                                            <option value="">Select Account</option>
                                            {expenseAccounts.map((account) => (
                                                <option key={account.account_id} value={account.account_id}>
                                                    {account.account_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* ITC & TDS */}
                                    <div className="space-y-2">
                                        <h6 className="text-sm font-medium text-gray-700">ITC & TDS</h6>

                                        {/* ITC Eligibility */}
                                        <div>
                                            <label className="text-gray-600 text-xs">ITC Eligibility:</label>
                                            <select
                                                value={item.itc_eligibility || ''}
                                                onChange={(e) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        line_items: prev.line_items.map((lineItem, idx) =>
                                                            idx === index ? {
                                                                ...lineItem,
                                                                itc_eligibility: e.target.value
                                                            } : lineItem
                                                        )
                                                    }));
                                                }}
                                                className="w-full border rounded-md px-2 py-1 text-xs"
                                            >
                                                <option value="eligible">Eligible</option>
                                                <option value="ineligible_section17">Ineligible - Section 17(5)</option>
                                                <option value="ineligible_others">Ineligible - Others</option>
                                            </select>
                                        </div>

                                        {/* TDS for this line item */}
                                        <div>
                                            <label className="flex items-center gap-1 text-xs">
                                                <input
                                                    type="checkbox"
                                                    checked={item.tds_applicable || false}
                                                    onChange={(e) => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            line_items: prev.line_items.map((lineItem, idx) =>
                                                                idx === index ? {
                                                                    ...lineItem,
                                                                    tds_applicable: e.target.checked,
                                                                    tds_rate: e.target.checked ? (lineItem.tds_rate || 0) : 0,
                                                                    tds_amount: e.target.checked ?
                                                                        ((lineItem.amount * (lineItem.tds_rate || 0)) / 100) : 0
                                                                } : lineItem
                                                            )
                                                        }));
                                                    }}
                                                    className="rounded"
                                                />
                                                TDS Applicable
                                            </label>
                                        </div>

                                        {item.tds_applicable && (
                                            <div>
                                                <label className="text-gray-600 text-xs">TDS Rate:</label>
                                                <select
                                                    value={item.tds_tax_id || ''}
                                                    onChange={(e) => {
                                                        const selectedTds = tdsRates.find(tds => tds.tax_id === e.target.value);
                                                        if (selectedTds) {
                                                            const rate = parseFloat(selectedTds.value);
                                                            const amount = (item.amount * rate) / 100;

                                                            setFormData(prev => ({
                                                                ...prev,
                                                                line_items: prev.line_items.map((lineItem, idx) =>
                                                                    idx === index ? {
                                                                        ...lineItem,
                                                                        tds_rate: rate,
                                                                        tds_amount: amount,
                                                                        tds_tax_id: selectedTds.tax_id,
                                                                        tds_tax_name: selectedTds.tax_name
                                                                    } : lineItem
                                                                )
                                                            }));
                                                        }
                                                    }}
                                                    className="w-full border rounded-md px-2 py-1 text-xs"
                                                >
                                                    <option value="">Select TDS</option>
                                                    {tdsRates.map((rate) => (
                                                        <option key={rate.tax_id} value={rate.tax_id}>
                                                            {rate.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {item.tds_amount > 0 && (
                                                    <div className="text-xs text-purple-600 mt-1">
                                                        TDS Amount: ₹{item.tds_amount.toFixed(2)}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600">
                                    Tax: ₹{item.total_tax.toFixed(2)} |
                                    {item.tds_applicable && item.tds_amount > 0 && ` TDS: ₹${item.tds_amount.toFixed(2)} | `}
                                    Total: ₹{item.total_with_tax.toFixed(2)}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Enhanced Summary */}
                {/* Update the summary section in renderItemsTab() */}
                <div className="bg-blue-50 border border-blue-200 rounded p-4">
                    <h5 className="font-medium text-blue-900 mb-2">Line Items Summary</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <span className="text-blue-600">Total Items:</span>
                            <div className="font-medium">{formData.line_items.length}</div>
                        </div>
                        <div>
                            <span className="text-blue-600">Accounts Assigned:</span>
                            <div className="font-medium text-green-600">
                                {formData.line_items.filter(item => item.expense_account_id && item.expense_account_id.trim() !== '').length} / {formData.line_items.length}
                            </div>
                        </div>
                        <div>
                            <span className="text-blue-600">TDS Items:</span>
                            <div className="font-medium text-purple-600">
                                {formData.line_items.filter(item => item.tds_applicable).length}
                            </div>
                        </div>
                        <div>
                            <span className="text-blue-600">Total TDS:</span>
                            <div className="font-medium text-purple-600">
                                ₹{formData.line_items.reduce((sum, item) => sum + (item.tds_amount || 0), 0).toFixed(2)}
                            </div>
                        </div>
                    </div>

                    {/* Show deletion warning if only one item left */}
                    {formData.line_items.length === 1 && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                            <div className="text-xs text-yellow-700">
                                ⚠️ This is the last line item. Deleting it will create a default item.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );

        // Terms tab content
        const renderTermsTab = () => (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Enhanced Bill Dates */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Bill Dates & Terms</h4>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Invoice Number *
                            </label>
                            <input
                                type="text"
                                value={formData.invoice_number || purchase.invoice_no}
                                onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                                className="w-full border rounded-md px-3 py-2"
                                placeholder="Enter invoice number"
                                required
                            />
                            {!formData.invoice_number && !purchase.invoice_no && (
                                <div className="text-xs text-red-600 mt-1">Invoice number is required</div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Bill Date *
                            </label>
                            <input
                                type="date"
                                value={formData.bill_date}
                                onChange={(e) => {
                                    console.log(`📅 Bill date changed to: ${e.target.value}`);
                                    setFormData(prev => ({ ...prev, bill_date: e.target.value }));
                                }}
                                className="w-full border rounded-md px-3 py-2"
                                required
                            />
                            {!formData.bill_date && (
                                <div className="text-xs text-red-600 mt-1">Bill date is required</div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Payment Terms *
                            </label>
                            <select
                                value={formData.payment_terms}
                                onChange={(e) => {
                                    console.log(`💳 Payment terms changed to: ${e.target.value}`);
                                    setFormData(prev => ({ ...prev, payment_terms: e.target.value }));
                                }}
                                className="w-full border rounded-md px-3 py-2"
                                required
                            >
                                <option value="">Select Payment Terms</option>
                                {paymentTermsOptions.map((term) => (
                                    <option key={term} value={term}>{term}</option>
                                ))}
                            </select>
                            {!formData.payment_terms && (
                                <div className="text-xs text-red-600 mt-1">Payment terms are required</div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Due Date
                                {formData.bill_date && formData.payment_terms && (
                                    <span className="text-xs text-green-600 ml-1">(Auto-calculated)</span>
                                )}
                            </label>
                            <input
                                type="date"
                                value={formData.due_date}
                                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                                className="w-full border rounded-md px-3 py-2"
                            />
                            {formData.due_date && (
                                <div className="text-xs text-gray-500 mt-1">
                                    Due in {Math.ceil((new Date(formData.due_date) - new Date(formData.bill_date || new Date())) / (1000 * 60 * 60 * 24))} days
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Enhanced Notes */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Additional Information</h4>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notes
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                className="w-full border rounded-md px-3 py-2"
                                rows="6"
                                placeholder="Add any additional notes about this purchase..."
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                Notes will not be shown in PDF bills but will be saved for internal reference
                            </div>
                        </div>

                        {/* Purchase Info Display */}
                        <div className="bg-gray-50 p-3 rounded border">
                            <div className="text-sm font-medium text-gray-700 mb-2">Original Purchase Details</div>
                            <div className="space-y-1 text-xs text-gray-600">
                                <div><strong>Vendor:</strong> {purchase.vendor_name}</div>
                                <div><strong>GSTIN:</strong> {purchase.gstin || 'Not provided'}</div>
                                <div><strong>Currency:</strong> {purchase.currency}</div>
                                {purchase.Email_address && <div><strong>Email:</strong> {purchase.Email_address}</div>}
                                {purchase.Phone && <div><strong>Phone:</strong> {purchase.Phone}</div>}
                                {purchase.Payment_Terms && <div><strong>Original Terms:</strong> {purchase.Payment_Terms}</div>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Date Calculations Summary */}
                {formData.bill_date && formData.due_date && (
                    <div className="bg-green-50 border border-green-200 rounded p-4">
                        <h5 className="font-medium text-green-900 mb-2">Date Summary</h5>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                                <span className="text-green-600">Bill Date:</span>
                                <div className="font-medium">{new Date(formData.bill_date).toLocaleDateString()}</div>
                            </div>
                            <div>
                                <span className="text-green-600">Due Date:</span>
                                <div className="font-medium">{new Date(formData.due_date).toLocaleDateString()}</div>
                            </div>
                            <div>
                                <span className="text-green-600">Credit Period:</span>
                                <div className="font-medium">
                                    {Math.ceil((new Date(formData.due_date) - new Date(formData.bill_date)) / (1000 * 60 * 60 * 24))} days
                                </div>
                            </div>
                        </div>

                        {/* Late Payment Warning */}
                        {new Date(formData.due_date) < new Date() && (
                            <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded">
                                <div className="text-xs text-red-700 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    Warning: Due date is in the past
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );

        const renderTabContent = () => {
            switch (activeTab) {
                case 'vendor':
                    return renderVendorTab();
                case 'items':
                    return renderItemsTab();
                // case 'tax':
                //     return renderTaxTab();
                case 'terms':
                    return renderTermsTab();
                default:
                    return renderVendorTab();
            }
        };

        const canSave = () => {
            const basicValidation = formData.zoho_vendor_id &&
                formData.bill_date &&
                formData.payment_terms &&
                (formData.line_items || []).every(item =>
                    item.expense_account_id && item.expense_account_id.trim() !== ''
                );

            // Check TDS validation for items that have TDS applicable
            const tdsValidation = (formData.line_items || []).every(item => {
                if (item.tds_applicable && parseFloat(item.tds_rate || 0) > 0) {
                    return item.tds_tax_id && item.tds_tax_id.trim() !== '';
                }
                return true; // If TDS not applicable, it's valid
            });

            return basicValidation && tdsValidation;
        };

        const handleSave = () => {
            if (!formData.zoho_vendor_id) {
                alert('⚠️ Please select a vendor before saving');
                setActiveTab('vendor');
                return;
            }

            if (!formData.bill_date) {
                alert('⚠️ Please set the bill date');
                setActiveTab('terms');
                return;
            }

            if (!formData.payment_terms) {
                alert('⚠️ Please select payment terms');
                setActiveTab('terms');
                return;
            }

            // FIXED: Clear TDS validation message
            if (formData.tds_applicable && formData.tds_rate > 0 && !formData.tds_tax_id) {
                alert('⚠️ Please select a specific TDS tax type when TDS is applicable');
                setActiveTab('tax');
                return;
            }

            const saveData = {
                vendor_id: formData.zoho_vendor_id,
                vendor_name: formData.zoho_vendor_name,
                expense_account_id: formData.zoho_expense_account_id,
                expense_account_name: formData.zoho_expense_account_name,
                reverse_charge_applicable: formData.reverse_charge_applicable,

                // SIMPLIFIED TDS data structure
                tds_applicable: Boolean(formData.tds_applicable),
                tds_rate: parseFloat(formData.tds_rate || 0),
                tds_amount: parseFloat(formData.tds_amount || 0),
                tds_tax_id: formData.tds_tax_id || null,
                tds_tax_name: formData.tds_tax_name || null,

                discount_amount: formData.discount_amount,
                discount_percentage: formData.discount_percentage,
                is_inclusive_tax: formData.is_inclusive_tax,
                payment_terms: formData.payment_terms,
                notes: formData.notes,
                bill_date: formData.bill_date,
                due_date: formData.due_date,
                invoice_number: formData.invoice_number,
                // ✅ ENSURE ALL NEW FIELDS ARE INCLUDED IN LINE ITEMS
                line_items_data: JSON.stringify(formData.line_items.map(item => ({
                    ...item,
                    itc_eligibility: item.itc_eligibility || '',
                    // Include all editable tax rates
                    sgst_rate: parseFloat(item.sgst_rate || 0),
                    cgst_rate: parseFloat(item.cgst_rate || 0),
                    igst_rate: parseFloat(item.igst_rate || 0),
                    // Recalculated tax amounts
                    sgst_amount: (parseFloat(item.amount || 0) * parseFloat(item.sgst_rate || 0)) / 100,
                    cgst_amount: (parseFloat(item.amount || 0) * parseFloat(item.cgst_rate || 0)) / 100,
                    igst_amount: (parseFloat(item.amount || 0) * parseFloat(item.igst_rate || 0)) / 100,
                    total_tax: ((parseFloat(item.amount || 0) * parseFloat(item.sgst_rate || 0)) / 100) +
                        ((parseFloat(item.amount || 0) * parseFloat(item.cgst_rate || 0)) / 100) +
                        ((parseFloat(item.amount || 0) * parseFloat(item.igst_rate || 0)) / 100),
                    total_with_tax: parseFloat(item.amount || 0) +
                        ((parseFloat(item.amount || 0) * parseFloat(item.sgst_rate || 0)) / 100) +
                        ((parseFloat(item.amount || 0) * parseFloat(item.cgst_rate || 0)) / 100) +
                        ((parseFloat(item.amount || 0) * parseFloat(item.igst_rate || 0)) / 100)
                })) || [])
            };

            console.log('💾 Saving simplified purchase data:', saveData);
            onSave(saveData);
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-screen overflow-y-auto">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-semibold">Edit Purchase #{purchase.invoice_no}</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Tab Navigation */}
                    <div className="border-b mb-6">
                        <div className="flex">
                            {tabs.map((tab) => {
                                const IconComponent = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 ${activeTab === tab.id
                                            ? 'border-blue-500 text-blue-600 bg-blue-50'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        <IconComponent className="w-4 h-4" />
                                        {tab.name}
                                        {tab.id === 'tax' && formData.tds_applicable && formData.tds_rate > 0 && !formData.tds_tax_id && (
                                            <AlertTriangle className="w-3 h-3 text-red-500" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tab Content */}
                    {renderTabContent()}

                    {/* Enhanced Purchase Summary */}
                    <div className="mt-6 bg-gray-50 border border-gray-200 rounded p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Purchase Summary</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">Invoice Number:</span>
                                <div className="font-medium">{purchase.invoice_no}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Amount:</span>
                                <div className="font-medium">₹{parseFloat(purchase.amount || 0).toLocaleString()}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Total:</span>
                                <div className="font-medium">₹{parseFloat(purchase.total || 0).toLocaleString()}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Tax Amount:</span>
                                <div className="font-medium text-blue-600">
                                    ₹{(parseFloat(purchase.total || 0) - parseFloat(purchase.amount || 0)).toFixed(2)}
                                </div>
                            </div>
                        </div>

                        {/* TDS & Reverse Charge Summary */}
                        {(formData.reverse_charge_applicable || (formData.tds_applicable && formData.tds_rate > 0)) && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    {formData.reverse_charge_applicable && (
                                        <div>
                                            <span className="text-orange-600">Reverse Charge:</span>
                                            <div className="font-medium text-orange-700">Applied</div>
                                        </div>
                                    )}
                                    {formData.tds_applicable && formData.tds_rate > 0 && (
                                        <>
                                            <div>
                                                <span className="text-purple-600">TDS Rate:</span>
                                                <div className="font-medium text-purple-700">{parseFloat(formData.tds_rate || 0)}%</div>
                                            </div>
                                            <div>
                                                <span className="text-purple-600">TDS Amount:</span>
                                                <div className="font-medium text-purple-700">₹{(parseFloat(formData.tds_amount) || 0).toFixed(2)}</div>
                                            </div>
                                            <div>
                                                <span className="text-purple-600">TDS Tax:</span>
                                                <div className="font-medium text-purple-700">
                                                    {formData.tds_tax_name || 'Not Selected'}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!canSave()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <Save className="w-4 h-4" />
                            Save Changes
                            {formData.tds_applicable && formData.tds_rate > 0 && !formData.tds_tax_id && (
                                <AlertTriangle className="w-4 h-4 text-yellow-300" />
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const fetchPurchases = async () => {
        try {
            if (!currentOrganization?.organization_id) {
                console.log('❌ No organization ID available, cannot fetch purchases');
                return;
            }

            console.log(`🔍 Fetching purchases for company: ${currentOrganization.organization_id}`);

            const response = await fetch(`${API_BASE_URL}/purchases/with-expense-data?company_id=${currentOrganization.organization_id}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success && Array.isArray(data.data)) {
                console.log(`✅ Fetched ${data.data.length} purchases for company ${currentOrganization.organization_id}`);

                const purchasesWithValidation = data.data.map(purchase => ({
                    ...purchase,
                    validationStatus: calculateValidationStatus(purchase, data.data)
                }));
                setPurchases(purchasesWithValidation);
            } else {
                console.error('❌ Failed to fetch purchases:', data.message);
                alert('Failed to fetch purchases: ' + (data.message || 'Unknown error'));
                setPurchases([]); // Set empty array on error
            }
        } catch (error) {
            console.error('❌ Error fetching purchases:', error);
            alert('Error fetching purchases: ' + error.message);
            setPurchases([]); // Set empty array on error
        }
    };

    // FIXED: Fetch vendors using correct endpoint
    const fetchVendors = async () => {
        if (!currentOrganization?.organization_id) {
            console.log('❌ No organization ID available, skipping vendors fetch');
            return;
        }
        try {
            console.log(`🔍 Fetching vendors for company: ${currentOrganization.organization_id}`);

            // FIXED: Use the correct endpoint that exists in your backend routes
            const response = await fetch(`${API_BASE_URL}/vendors_purchase?company_id=${currentOrganization.organization_id}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success && Array.isArray(data.vendors)) {
                console.log(`✅ Fetched ${data.vendors.length} vendors for company ${currentOrganization.organization_id}`);
                setVendors(data.vendors);
            } else {
                console.error('❌ Failed to fetch vendors:', data.message);
                console.log('Setting empty vendors array');
                setVendors([]); // Set empty array on error
            }
        } catch (error) {
            console.error('❌ Error fetching vendors:', error);
            setVendors([]); // Set empty array on error
        }
    };

    const fetchExpenseAccounts = async () => {
        if (!currentOrganization?.organization_id) {
            console.log('❌ No organization ID available, skipping expense accounts fetch');
            return;
        }

        try {
            console.log(`🔍 Fetching expense accounts for company: ${currentOrganization.organization_id}`);

            const response = await fetch(`${API_BASE_URL}/expense-accounts?company_id=${currentOrganization.organization_id}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success && Array.isArray(data.accounts)) {
                console.log(`✅ Fetched ${data.accounts.length} expense accounts for company ${currentOrganization.organization_id}`);
                setExpenseAccounts(data.accounts);
            } else {
                console.error('❌ Failed to fetch expense accounts:', data.message);
                setExpenseAccounts([]); // Set empty array on error
            }
        } catch (error) {
            console.error('❌ Error fetching expense accounts:', error);
            setExpenseAccounts([]); // Set empty array on error
        }
    };

    // FIXED: Fetch TDS rates from local taxes table
    const fetchTdsRates = async () => {
        if (!currentOrganization?.organization_id) {
            console.log('❌ No organization ID available, skipping TDS rates fetch');
            return;
        }

        try {
            console.log(`🔍 Fetching TDS rates from Zoho for company: ${currentOrganization.organization_id}`);

            // Check token status first
            const tokenResponse = await fetch(`${API_BASE_URL}/token/status`, {
                headers: getAuthHeaders()
            });
            const tokenStatus = await tokenResponse.json();

            if (!tokenStatus?.success || !tokenStatus?.data?.token_valid) {
                console.log('❌ No valid Zoho token, falling back to local TDS rates');
                // Fallback to local database
                const localResponse = await fetch(`${API_BASE_URL}/tds-rates?company_id=${currentOrganization.organization_id}`, {
                    headers: getAuthHeaders()
                });
                const localData = await localResponse.json();
                if (localData.success) {
                    setTdsRates(localData.tds_rates);
                }
                return;
            }

            // Fetch TDS rates from Zoho using correct endpoint
            const response = await fetch(`${API_BASE_URL}/zoho-tds-rates?company_id=${currentOrganization.organization_id}`, {
                headers: getAuthHeaders()
            });

            const data = await response.json();

            if (data.success && Array.isArray(data.tds_rates)) {
                console.log(`✅ Fetched ${data.tds_rates.length} TDS rates from Zoho`);
                setTdsRates(data.tds_rates);
            } else {
                console.log('❌ Failed to fetch TDS rates from Zoho, using fallback');
                // Fallback to local database
                const localResponse = await fetch(`${API_BASE_URL}/tds-rates?company_id=${currentOrganization.organization_id}`, {
                    headers: getAuthHeaders()
                });
                const localData = await localResponse.json();
                if (localData.success) {
                    setTdsRates(localData.tds_rates);
                }
            }
        } catch (error) {
            console.error('❌ Error fetching TDS rates:', error);
            setTdsRates([]);
        }
    };

    const updatePurchaseValidation = async (purchaseId, updates) => {
        try {
            console.log('🔄 Sending update data to backend:', updates);

            const cleanUpdates = {
                vendor_id: updates.vendor_id || null,
                vendor_name: updates.vendor_name || null,
                expense_account_id: updates.expense_account_id || null,
                expense_account_name: updates.expense_account_name || null,
                reverse_charge_applicable: Boolean(updates.reverse_charge_applicable),
                tds_applicable: Boolean(updates.tds_applicable),
                tds_rate: parseFloat(updates.tds_rate || 0),
                tds_amount: parseFloat(updates.tds_amount || 0),
                tds_tax_id: updates.tds_tax_id || null, // CHANGED from tds_account_id
                tds_tax_name: updates.tds_tax_name || null, // CHANGED from tds_account_name
                discount_amount: parseFloat(updates.discount_amount || 0),
                discount_percentage: parseFloat(updates.discount_percentage || 0),
                is_inclusive_tax: Boolean(updates.is_inclusive_tax),
                payment_terms: updates.payment_terms || '',
                notes: updates.notes || '',
                bill_date: updates.bill_date || null,
                due_date: updates.due_date || null,
                line_items_data: updates.line_items_data || null,
                validation_status: 'updated'
            };

            console.log('🔍 Clean updates being sent:', cleanUpdates);

            const response = await fetch(`${API_BASE_URL}/purchases/${purchaseId}/expense-validation`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(cleanUpdates)
            });

            const data = await response.json();

            if (data.success) {
                await fetchPurchases();
                console.log('✅ Purchase updated and data refreshed');
                console.log('📊 Update verification:', data.updated_fields);
            } else {
                alert('Failed to update purchase: ' + data.message);
                console.error('❌ Update failed:', data);
            }
        } catch (error) {
            console.error('❌ Error updating purchase:', error);
            alert('Error updating purchase: ' + error.message);
        }
    };

    // FIXED syncSelectedPurchases function with correct TDS data structure
    const syncSelectedPurchases = async () => {
        if (selectedPurchases.length === 0) {
            alert('No purchases selected for sync.');
            return;
        }

        // Filter for valid purchases that can be synced (approved only)
        const validPurchases = purchases.filter(p => {
            const isSelected = selectedPurchases.includes(p.id);
            const canSyncPurchase = canSync(p);
            const isNotSynced = !p.zoho_bill_id && p.zoho_sync_status !== 'synced';
            const isValid = p.validationStatus?.isValid && !p.validationStatus?.isDuplicate;

            return isSelected && canSyncPurchase && isNotSynced && isValid;
        });

        // Check if any selected purchases are not approved
        const unapprovedSelected = purchases.filter(p =>
            selectedPurchases.includes(p.id) && !canSync(p)
        );

        if (unapprovedSelected.length > 0) {
            const statusCounts = unapprovedSelected.reduce((acc, purchase) => {
                const status = purchase.approver_status || purchase.approvalInfo?.status || 'unknown';
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});

            let message = 'Cannot sync the following purchases:\n';
            Object.entries(statusCounts).forEach(([status, count]) => {
                const statusText = status === 'pending_approval' ? 'Pending Approval' :
                    status === 'rejected' ? 'Rejected' : 'Unknown Status';
                message += `• ${count} purchases with ${statusText}\n`;
            });
            message += '\nOnly approved purchases can be synced.';

            alert(message);
            return;
        }

        if (validPurchases.length === 0) {
            alert('No valid approved purchases selected for sync.');
            return;
        }

        setSyncInProgress(true);

        try {
            const billUpdates = validPurchases.map(p => ({
                purchase_id: p.id,
                company_id: currentOrganization.organization_id,
                vendor_id: p.zoho_vendor_id,
                vendor_name: p.zoho_vendor_name || p.vendor_name,
                expense_account_id: p.zoho_expense_account_id,
                expense_account_name: p.zoho_expense_account_name,
                bill_number: p.invoice_no,
                bill_date: p.date,
                due_date: p.due_date || calculateDueDate(p.date, 30),
                item_description: p.item,
                amount: parseFloat(p.amount || 0),
                total_amount: parseFloat(p.total),
                reverse_charge_applicable: p.reverse_charge_applicable || false,

                // FIXED: Simplified TDS data structure
                tds_applicable: Boolean(p.tds_applicable),
                tds_rate: parseFloat(p.tds_rate || 0),
                tds_amount: parseFloat(p.tds_amount || 0),
                tds_tax_id: p.tds_tax_id || null,
                tds_tax_name: p.tds_tax_name || null,

                discount_amount: p.discount_amount || 0,
                is_inclusive_tax: p.is_inclusive_tax === 1,
                notes: p.notes || `Purchase from ${p.vendor_name}`,
                terms: p.Payment_Terms || 'As per agreement',
                currency_code: p.currency || 'INR',
                attachment_url: p.AWS_File_link || null,
                gst_details: {
                    sgst_rate: parseFloat(p.sgst || 0),
                    cgst_rate: parseFloat(p.cgst || 0),
                    igst_rate: parseFloat(p.igst || 0),
                    total_gst_rate: parseFloat(p.sgst || 0) + parseFloat(p.cgst || 0) + parseFloat(p.igst || 0)
                }
            }));

            console.log('🔍 Bill updates being sent:', billUpdates);

            const response = await fetch(`${API_BASE_URL}/bills/sync-bills-complete-new`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ billUpdates })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                await fetchPurchases(); // Refresh the data
                const successfulIds = data.results?.filter(r => r.success)?.map(r => r.purchase_id) || [];
                setSelectedPurchases(prev => prev.filter(id => !successfulIds.includes(id)));

                alert(`Successfully synced ${successfulIds.length} bills!`);

                if (data.results?.some(r => !r.success)) {
                    const failures = data.results.filter(r => !r.success);
                    console.error('Some syncs failed:', failures);
                    alert(`Note: ${failures.length} bills failed to sync. Check console for details.`);
                }
            } else {
                throw new Error(data.message || 'Sync failed');
            }
        } catch (error) {
            console.error('Sync error:', error);
            alert('Sync error: ' + error.message);
        } finally {
            setSyncInProgress(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const getStatusIcon = (purchase, isDuplicate) => {
        if (isDuplicate) return <Copy className="w-5 h-5 text-orange-500" />;
        if (purchase.zoho_bill_id || purchase.zoho_sync_status === 'synced') return <CheckCircle className="w-5 h-5 text-green-500" />;
        if (purchase.zoho_sync_status === 'failed') return <XCircle className="w-5 h-5 text-red-500" />;
        if (purchase.validationStatus?.isValid) return <Clock className="w-5 h-5 text-blue-500" />;
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    };

    const getStatusText = (purchase, isDuplicate) => {
        if (isDuplicate) return 'Duplicate';
        if (purchase.zoho_bill_id || purchase.zoho_sync_status === 'synced') return 'Synced';
        if (purchase.zoho_sync_status === 'failed') return 'Failed';
        if (purchase.validationStatus?.isValid) return 'Ready';
        return 'Pending';
    };

    const getCompletionColor = (percentage) => {
        if (percentage >= 100) return 'bg-green-500';
        if (percentage >= 75) return 'bg-blue-500';
        if (percentage >= 50) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const calculateDueDate = (billDate, paymentTerms = 30) => {
        const date = new Date(billDate);
        date.setDate(date.getDate() + paymentTerms);
        return date.toISOString().split('T')[0];
    };

    // Filter purchases based on status
    const filteredPurchases = purchases.filter(purchase => {
        const isDuplicate = purchase.validationStatus?.isDuplicate;
        const isSynced = purchase.zoho_bill_id || purchase.zoho_sync_status === 'synced';
        const isFailed = purchase.zoho_sync_status === 'failed';
        const isReady = purchase.validationStatus?.isValid && !isSynced && !isDuplicate;
        const isPending = !purchase.validationStatus?.isValid && !isSynced && !isDuplicate;

        switch (filter) {
            case 'ready': return isReady;
            case 'synced': return isSynced;
            case 'failed': return isFailed;
            case 'pending': return isPending;
            case 'duplicates': return isDuplicate;
            default: return true;
        }
    });

    const stats = {
        total: purchases.length,
        ready: purchases.filter(p => p.validationStatus?.isValid && !p.zoho_bill_id && !p.validationStatus?.isDuplicate).length,
        synced: purchases.filter(p => p.zoho_bill_id || p.zoho_sync_status === 'synced').length,
        failed: purchases.filter(p => p.zoho_sync_status === 'failed').length,
        pending: purchases.filter(p => !p.validationStatus?.isValid && !p.zoho_bill_id && !p.validationStatus?.isDuplicate).length,
        duplicates: purchases.filter(p => p.validationStatus?.isDuplicate).length
    };

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Purchase Bills Integration Manager</h1>
                        <p className="text-gray-600 mt-2">
                            Enhanced validation with GST compliance, TDS, reverse charge, and complete bill sync workflow
                        </p>
                        <p className="text-gray-600 mt-1">
                            Organization: <span className="font-medium text-blue-600">{currentOrganization.name}</span>
                            {" "}({currentOrganization.organization_id})
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Add Auto-Processing Button
                        <button
                            onClick={autoProcessPurchases}
                            disabled={autoProcessing || loading}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                        >
                            {autoProcessing ? (
                                <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Auto-Processing...
                                </>
                            ) : (
                                <>
                                    <Brain className="w-4 h-4" />
                                    Auto-Process Vendors
                                </>
                            )}
                        </button> */}

                        <button
                            onClick={loadAllData}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            Refresh All Data
                        </button>
                    </div>
                </div>

                {/* Add Auto-Processing Results Display */}
                {autoProcessResults && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3">Latest Auto-Processing Results</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="bg-white p-3 rounded border">
                                <div className="text-2xl font-bold text-green-600">{autoProcessResults.summary.successful}</div>
                                <div className="text-sm text-gray-600">Successful</div>
                            </div>
                            <div className="bg-white p-3 rounded border">
                                <div className="text-2xl font-bold text-red-600">{autoProcessResults.summary.failed}</div>
                                <div className="text-sm text-gray-600">Failed</div>
                            </div>
                            <div className="bg-white p-3 rounded border">
                                <div className="text-2xl font-bold text-blue-600">{autoProcessResults.summary.total}</div>
                                <div className="text-sm text-gray-600">Total Processed</div>
                            </div>
                        </div>

                        {/* Show detailed results */}
                        <div className="max-h-32 overflow-y-auto">
                            {autoProcessResults.results.map((result, index) => (
                                <div key={index} className={`text-sm p-2 rounded mb-1 ${result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    <span className="font-medium">#{result.invoice_no}</span> - {result.vendor_name}
                                    {result.success ? (
                                        <span className="ml-2">
                                            ✅ {result.vendor_action} vendor, Score: {result.validation_score}
                                        </span>
                                    ) : (
                                        <span className="ml-2">❌ {result.error}</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={() => setAutoProcessResults(null)}
                            className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                        >
                            Dismiss Results
                        </button>
                    </div>
                )}

                {/* Stats Dashboard */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <FileText className="w-8 h-8 text-gray-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900">{stats.total}</h3>
                                <p className="text-sm text-gray-600">Total Purchases</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <Clock className="w-8 h-8 text-blue-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-blue-900">{stats.ready}</h3>
                                <p className="text-sm text-blue-600">Ready to Sync</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-green-900">{stats.synced}</h3>
                                <p className="text-sm text-green-600">Synced</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <XCircle className="w-8 h-8 text-red-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-red-900">{stats.failed}</h3>
                                <p className="text-sm text-red-600">Failed</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <Copy className="w-8 h-8 text-orange-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-orange-900">{stats.duplicates}</h3>
                                <p className="text-sm text-orange-600">Duplicates</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="w-8 h-8 text-yellow-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-yellow-900">{stats.pending}</h3>
                                <p className="text-sm text-yellow-600">Pending</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters and Actions */}
            <div className="bg-white rounded-lg border p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-2">
                        <Filter className="w-5 h-5 text-gray-500" />
                        <div className="flex gap-2">
                            {['all', 'pending', 'ready', 'synced', 'failed', 'duplicates'].map(filterType => (
                                <button
                                    key={filterType}
                                    onClick={() => setFilter(filterType)}
                                    className={`px-3 py-1 rounded-full text-sm font-medium ${filter === filterType
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                                    {filterType === 'duplicates' && stats.duplicates > 0 && (
                                        <span className="ml-1 bg-orange-200 text-orange-800 px-1 rounded-full text-xs">
                                            {stats.duplicates}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        {selectedPurchases.length > 0 && (
                            <span className="text-sm text-gray-600">
                                {selectedPurchases.length} selected
                            </span>
                        )}

                        <button
                            onClick={() => {
                                const readyIds = filteredPurchases
                                    .filter(p => p.validationStatus?.isValid && !p.zoho_bill_id && !p.validationStatus?.isDuplicate)
                                    .map(p => p.id);
                                setSelectedPurchases(readyIds);
                            }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Select All Ready ({stats.ready})
                        </button>

                        <button
                            onClick={() => setSelectedPurchases([])}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Clear Selection
                        </button>

                        {selectedPurchases.length > 0 && (
                            <button
                                onClick={syncSelectedPurchases}
                                disabled={syncInProgress}
                                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                            >
                                {syncInProgress ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Syncing...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Sync {selectedPurchases.length} as Bills
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Purchase Records Table */}
            <div className="bg-white rounded-lg border">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">Purchase Records</h2>
                    <p className="text-gray-600 mt-1">Enhanced validation with GST, TDS, reverse charge, and duplicate detection</p>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="text-left p-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedPurchases.length === filteredPurchases.filter(p =>
                                            p.validationStatus?.isValid &&
                                            !p.zoho_bill_id &&
                                            !p.validationStatus?.isDuplicate
                                        ).length &&
                                            filteredPurchases.filter(p =>
                                                p.validationStatus?.isValid &&
                                                !p.zoho_bill_id &&
                                                !p.validationStatus?.isDuplicate
                                            ).length > 0}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const readyIds = filteredPurchases
                                                    .filter(p => p.validationStatus?.isValid && !p.zoho_bill_id && !p.validationStatus?.isDuplicate)
                                                    .map(p => p.id);
                                                setSelectedPurchases(readyIds);
                                            } else {
                                                setSelectedPurchases([]);
                                            }
                                        }}
                                        className="rounded"
                                    />
                                </th>
                                <th className="text-left p-4">Status & Progress</th>
                                <th className="text-left p-4">Purchase Details</th>
                                <th className="text-left p-4">Vendor Validation</th>
                                <th className="text-left p-4">Account Mapping</th>
                                <th className="text-left p-4">Amount & Tax</th>
                                <th className="text-left p-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPurchases.map((purchase) => {
                                const isDuplicate = purchase.validationStatus?.isDuplicate;
                                const isFutureDate = isFutureBillDate(purchase.date);

                                return (
                                    <PurchaseRow
                                        key={purchase.id} // ADD KEY HERE TO FIX REACT WARNING
                                        purchase={purchase}
                                        vendors={vendors}
                                        expenseAccounts={expenseAccounts}
                                        isSelected={selectedPurchases.includes(purchase.id)}
                                        onToggleSelect={(id) => setSelectedPurchases(prev =>
                                            prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
                                        )}
                                        onEdit={(purchase) => {
                                            setEditingPurchase(purchase);
                                            setShowEditModal(true);
                                        }}
                                        formatCurrency={formatCurrency}
                                        getStatusIcon={getStatusIcon}
                                        getStatusText={getStatusText}
                                        getCompletionColor={getCompletionColor}
                                        isDuplicate={isDuplicate}
                                        isFutureDate={isFutureDate}
                                        // PASS THE APPROVAL FUNCTIONS AS PROPS
                                        canEdit={canEdit}
                                        canSync={canSync}
                                        getApprovalStatusDisplay={getApprovalStatusDisplay}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {filteredPurchases.length === 0 && (
                    <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No purchases found</h3>
                        <p className="text-gray-500">No purchases match the current filter criteria.</p>
                    </div>
                )}
            </div>

            {/* Enhanced Information Panel */}
            <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold mb-4">🤖 Auto-Processing Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="bg-purple-50 p-4 rounded">
                        <div className="font-medium text-purple-900 mb-2">Intelligent Vendor Matching</div>
                        <div className="text-purple-700">Searches existing vendors by name, GSTIN, and fuzzy matching</div>
                        <div className="text-xs text-purple-600 mt-1">70% similarity threshold for smart matching</div>
                    </div>
                    <div className="bg-indigo-50 p-4 rounded">
                        <div className="font-medium text-indigo-900 mb-2">Smart Vendor Creation</div>
                        <div className="text-indigo-700">Auto-creates vendors with proper GST treatment and state mapping</div>
                        <div className="text-xs text-indigo-600 mt-1">Handles overseas, business GST, and non-GST vendors</div>
                    </div>
                    <div className="bg-cyan-50 p-4 rounded">
                        <div className="font-medium text-cyan-900 mb-2">Payment Terms Intelligence</div>
                        <div className="text-cyan-700">Auto-detects and normalizes payment terms, calculates due dates</div>
                        <div className="text-xs text-cyan-600 mt-1">Supports 9+ payment term formats with auto-calculation</div>
                    </div>
                    <div className="bg-teal-50 p-4 rounded">
                        <div className="font-medium text-teal-900 mb-2">Validation Scoring</div>
                        <div className="text-teal-700">Calculates completion scores and validation status automatically</div>
                        <div className="text-xs text-teal-600 mt-1">100-point scoring system with detailed field analysis</div>
                    </div>
                </div>

                <div className="mt-4 p-3 bg-gray-100 rounded">
                    <div className="text-sm font-medium text-gray-800 mb-1">Auto-Processing Workflow:</div>
                    <div className="text-xs text-gray-600">
                        1. Search existing vendors → 2. Create if not found → 3. Set payment terms → 4. Calculate due dates → 5. Update validation scores
                    </div>
                </div>
            </div>

            {/* Edit Purchase Modal */}
            {showEditModal && editingPurchase && (
                <EditPurchaseModal
                    purchase={editingPurchase}
                    vendors={vendors}
                    expenseAccounts={expenseAccounts}
                    tdsRates={tdsRates}
                    currentOrganization={currentOrganization}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingPurchase(null);
                    }}
                    onSave={async (updates) => {
                        await updatePurchaseValidation(editingPurchase.id, updates);
                        setShowEditModal(false);
                        setEditingPurchase(null);
                        alert('Purchase updated successfully!');
                    }}
                    onCreateVendor={() => setShowVendorModal(true)}
                    fetchVendors={fetchVendors}
                    // PASS APPROVAL FUNCTIONS IF NEEDED IN MODAL
                    canEdit={canEdit}
                    canSync={canSync}
                />
            )}

            {/* Custom Vendor Creation Modal */}
            {showVendorModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                    <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="text-xl font-semibold">Create New Vendor</h2>
                            <button
                                onClick={() => setShowVendorModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-4">
                            <VendorManagement
                                isEmbeddedForm={true}
                                currentOrganization={currentOrganization}
                                onVendorCreated={(newVendor) => {
                                    if (newVendor) {
                                        fetchVendors();

                                        if (editingPurchase) {
                                            setEditingPurchase(prev => ({
                                                ...prev,
                                                zoho_vendor_id: newVendor.contact_id,
                                                zoho_vendor_name: newVendor.contact_name
                                            }));
                                        }
                                    }
                                    setShowVendorModal(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseBillsIntegrationManager;